var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/vault.ts
import { z } from "zod";

// src/ssrf.ts
import ipaddr from "ipaddr.js";
var SsrfError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "SsrfError";
  }
};
var ALLOWED_IPV4_RANGES = /* @__PURE__ */ new Set(["unicast"]);
var ALLOWED_IPV6_RANGES = /* @__PURE__ */ new Set(["unicast"]);
var isPrivateIp = (ip) => {
  if (!ipaddr.isValid(ip)) {
    return false;
  }
  let addr = ipaddr.parse(ip);
  if (addr.kind() === "ipv6" && addr.isIPv4MappedAddress()) {
    addr = addr.toIPv4Address();
  }
  const range = addr.range();
  if (addr.kind() === "ipv4") {
    return !ALLOWED_IPV4_RANGES.has(range);
  }
  return !ALLOWED_IPV6_RANGES.has(range);
};
var isIpLiteral = (host) => {
  const stripped = host.replace(/^\[/, "").replace(/\]$/, "");
  return ipaddr.isValid(stripped);
};
var BLOCKED_HOSTNAMES = /* @__PURE__ */ new Set([
  "localhost",
  "ip6-localhost",
  "ip6-loopback",
  "metadata",
  // GCP short name
  "metadata.google.internal"
]);
var validateWebhookUrl = (url, options = {}) => {
  const { requireHttps = true } = options;
  const allowedProtocols = options.allowedProtocols ?? (requireHttps ? ["https:"] : ["http:", "https:"]);
  const parsed = (() => {
    try {
      return new URL(url);
    } catch {
      throw new SsrfError("Invalid webhook URL");
    }
  })();
  if (!allowedProtocols.includes(parsed.protocol)) {
    throw new SsrfError(
      `Webhook URL protocol "${parsed.protocol}" is not allowed (allowed: ${allowedProtocols.join(", ")})`
    );
  }
  const host = parsed.hostname.toLowerCase();
  if (BLOCKED_HOSTNAMES.has(host) || host.endsWith(".localhost") || host.endsWith(".local")) {
    throw new SsrfError("Webhook URL host is not allowed");
  }
  const stripped = parsed.hostname.replace(/^\[/, "").replace(/\]$/, "");
  if (isIpLiteral(parsed.hostname) && isPrivateIp(stripped)) {
    throw new SsrfError("Webhook URL resolves to a private or reserved address");
  }
};
var isValidWebhookUrl = (url, options) => {
  try {
    validateWebhookUrl(url, options);
    return true;
  } catch {
    return false;
  }
};

// src/vault.ts
var processingMetadataSchema = z.object({
  compression: z.object({
    algorithm: z.enum(["zlib:pako", "none"])
  }).optional(),
  encryption: z.object({
    algorithm: z.enum(["aes-256-gcm", "ml-kem-768", "ml-kem-768-2", "ml-kem-768-argon2"]),
    // Optional algorithm for the user-password encryption layer. The primary
    // `algorithm` encrypts the high-entropy URL key layer (which needs no
    // memory-hard KDF); when a password is used, this hardens only that layer.
    // Absent on legacy entries, which encrypt both layers with `algorithm`.
    passwordAlgorithm: z.enum(["aes-256-gcm", "ml-kem-768", "ml-kem-768-2", "ml-kem-768-argon2"]).optional()
  })
}).describe("processing metadata including compression and encryption algorithms");
var vaultValueSchema = z.object({
  c: z.string().describe("encrypted content"),
  h: z.string().describe("sha256 hash of the encryption key + optional password"),
  m: processingMetadataSchema.optional(),
  b: z.boolean().describe("burn after reading"),
  dt: z.string().describe("delete token"),
  ttl: z.number().describe("time to live (TTL) in milliseconds"),
  cd: z.number().describe("created date time"),
  ips: z.string().describe("ip/cidr allow-list").optional(),
  rc: z.number().describe("maximum number of times the secret can be read").optional(),
  fc: z.number().min(1).max(10).describe("burn after n failed attempts").optional(),
  wh: z.object({
    u: z.string().url().refine((url) => isValidWebhookUrl(url, { requireHttps: false }), {
      message: "Webhook URL must be a public http(s) URL and must not target a private or reserved address"
    }).describe("url of the webhook"),
    n: z.string().max(50).describe("name of the secret").optional(),
    r: z.boolean().default(true).describe("should the webhook be called on read"),
    fpk: z.boolean().default(false).describe("should the webhook be called for failure to read based on password or key"),
    fip: z.boolean().default(false).describe("should the webhook be called for failure to read based on ip address"),
    b: z.boolean().default(false).describe("should the webhook be called for secret burn")
  }).describe("webhook configuration").optional()
});

// src/api.ts
import { z as z2 } from "zod";
var createVaultRequestSchema = vaultValueSchema.omit({
  cd: true,
  dt: true
});
var createVaultResponseSchema = z2.object({
  id: z2.string().describe("vault id"),
  dt: z2.string().describe("delete token")
});
var readVaultParamsSchema = z2.object({
  vaultId: z2.string()
});
var readVaultQuerySchema = z2.object({
  h: z2.string().describe("sha512 hash of the encryption key + optional password")
});
var readVaultResponseSchema = vaultValueSchema.pick({
  c: true,
  b: true,
  ttl: true,
  cd: true,
  m: true
});
var deleteVaultParamsSchema = z2.object({
  vaultId: z2.string()
});
var deleteVaultRequestSchema = z2.object({
  dt: z2.string()
});

// src/i18n/locales/en.ts
var en = {
  common: {
    createSecret: "Create Secret",
    starOnGithub: "Star on GitHub",
    features: "Features",
    learnMore: "Learn More",
    loading: "Loading...",
    create: "Create",
    delete: "Delete",
    copy: "Copy",
    share: "Share",
    download: "Download",
    success: "Success!",
    error: "Error",
    confirm: "Confirm",
    requestNewLanguage: "Request new or fix translations",
    footer: {
      tagline: "Built with security and privacy in mind, because ignorance can be bliss"
    },
    time: {
      minute_one: "{{count}} minute",
      minute_other: "{{count}} minutes",
      hour_one: "{{count}} hour",
      hour_other: "{{count}} hours",
      day_one: "{{count}} day",
      day_other: "{{count}} days"
    }
  },
  landing: {
    title: "Open-Source Zero-Knowledge Secret Sharing",
    subtitle: "Encrypt in your browser. Share once. Gone forever. Post-quantum end-to-end encryption \u2014 our servers never see your secrets.",
    pillars: {
      title: "Built for trust",
      zeroKnowledge: {
        title: "True zero-knowledge",
        description: "Encryption keys never leave your device. Decryption happens only in the recipient's browser \u2014 the server never sees plaintext."
      },
      ephemeral: {
        title: "Ephemeral by default",
        description: "Burn after reading, custom TTLs, and read limits so sensitive data does not linger longer than it should."
      },
      defense: {
        title: "Defense in depth",
        description: "ML-KEM post-quantum encryption, strict CSP, rate limits, and optional IP allow-lists \u2014 layered protection, not a single checkbox."
      },
      open: {
        title: "Open and auditable",
        description: "Fully open-source and designed to be self-hosted. Inspect the code, run your own instance, or contribute."
      }
    },
    alsoIncludes: {
      label: "Also includes",
      password: "password protection",
      files: "file sharing",
      webhooks: "webhooks",
      qrCode: "QR codes",
      ipControl: "IP allow-lists",
      readLimits: "read limits"
    },
    selfHost: {
      title: "Run your own instance",
      description: "Deploy the same zero-knowledge stack on your infrastructure \u2014 one click on Railway, or Docker Compose anywhere.",
      deployOnRailway: "Deploy on Railway",
      dockerCompose: "Docker Compose"
    },
    ecosystem: {
      cli: "CLI",
      chromeExtension: "Chrome Extension",
      github: "GitHub"
    },
    steps: {
      encrypt: {
        title: "1. Encrypt",
        description: "Your secret is encrypted in your browser before being stored"
      },
      share: {
        title: "2. Share",
        description: "Send the secure link to your intended recipient"
      },
      autoDelete: {
        title: "3. Auto-Delete",
        description: "Secret is permanently deleted after being viewed"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Secret content",
        placeholder: "Enter your secret content or file here...",
        fileHint: "add a file by drag-n-drop or clicking here (max 1 MB)",
        fileSelected: "File selected: {{name}} ({{size}} KB)",
        dropFile: "Drop file here",
        invalidFileType: "Invalid file type"
      },
      password: {
        label: "Password",
        placeholder: "Optional (but recommended)"
      },
      ttl: {
        label: "Time to live",
        placeholder: "Select expiration time"
      },
      burn: {
        label: "Burn after reading",
        description: "Guarantees the secret can be read only once \u2014 the first person to open the link"
      },
      advanced: {
        toggle: "advanced configuration",
        ip: {
          label: "IP/CIDR allow-list",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Restrict access to specific IP addresses or CIDR blocks (comma separated)"
        },
        readCount: {
          label: "Read count",
          description: "Maximum number of times the secret can be read"
        },
        failedAttempts: {
          label: "Burn after failed attempts",
          description: "Automatically delete the secret after a number of failed access attempts"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "Webhook URL to call when the secret is read, burned, or fails to read (configurable, if set)",
          read: "Read",
          burn: "Burn",
          failureToReadPK: "Failure (pass or key)",
          failureToReadIP: "Failure (IP or CIDR)",
          nameLabel: "Name",
          namePlaceholder: "Enter a name for this webhook",
          nameDescription: "A name to identify this webhook in notifications"
        }
      }
    },
    success: {
      title: "Secret Created!",
      description: {
        main: "Your secret has been created. Choose a sharing option below.",
        password: "Send the password through a separate channel.",
        separateKey: "For separate delivery, send the key-free URL and decryption key through different channels."
      },
      combinedUrl: "Combined URL (includes key)",
      keylessUrl: "Key-free URL",
      decryptionKey: "Decryption key",
      urlCopied: "URL copied to clipboard",
      keyCopied: "Decryption key copied to clipboard",
      secretDeleted: "Secret deleted",
      qrDownloaded: "QR code downloaded",
      qrDownloadFailed: "Failed to download QR code: {{error}}",
      qrCode: {
        title: "Secret URL QR Code",
        description: "Download and share the secret URL QR Code"
      },
      actions: {
        showUrl: "Show secret URL",
        hideUrl: "Hide secret URL",
        showKey: "Show decryption key",
        hideKey: "Hide decryption key",
        shareUrl: "Share secret URL",
        shareKey: "Share decryption key",
        copyUrl: "Copy secret URL",
        copyKey: "Copy decryption key",
        showQr: "Show combined URL QR code"
      },
      createAnother: "Create Another",
      deleteSecret: "Delete Secret",
      info: {
        expires: "Expires in: {{time}}",
        burn: "Secret will be deleted after it is viewed",
        passwordProtected: "Password protected",
        ipRestrictions: "IP restriction(s): {{ips}}",
        readCount: "Read count: {{count}}",
        webhook: "Webhook configured for: {{events}} ({{url}})",
        failureCount: "Secret will be deleted after {{count}} failed attempts"
      }
    },
    errors: {
      contentRequired: "Content is required",
      readCountWithBurn: "Read count cannot be used with burn after reading",
      tooManyIps: "Too many IP restrictions (max {{max}})",
      invalidIp: "Invalid IP address or CIDR block: {{ip}}",
      uploadFailed: "Failed to process dropped content: {{error}}",
      deleteFailed: "Failed to delete secret: {{error}}",
      secretNotFound: "secret not found",
      unexpectedStatus: "unexpected status code {{code}}",
      webhookConfigInvalid: "Webhook configuration is invalid - at least one webhook event type is required",
      fileSizeExceeded: "File is too large. Maximum size is {{max}}.",
      fileReadError: "Failed to read file",
      fileReadAborted: "File reading was aborted"
    }
  },
  view: {
    notFound: {
      title: "Secret Not Found",
      description: "This secret may have expired or been deleted.",
      createNew: "Create New Secret"
    },
    invalidLink: {
      title: "This link is invalid",
      description: "The link is incomplete or was altered in transit, so the secret cannot be decrypted. Ask the sender to share the full link again.",
      createNew: "Create New Secret"
    },
    connectionError: {
      title: "Couldn't reach the server",
      description: "The secret may still exist. Check your connection and try again.",
      tryAgain: "Try Again"
    },
    rateLimit: {
      title: "Too many requests",
      description: "You have made too many requests. Wait a moment and try again.",
      tryAgain: "Try Again"
    },
    password: {
      title: "Enter Password",
      label: "Password",
      placeholder: "Enter the password",
      description: "This secret is protected with a password - request from the sender",
      error: "Incorrect decryption key or password",
      required: "Enter a password.",
      show: "Show password",
      hide: "Hide password"
    },
    key: {
      title: "Enter Decryption Key",
      label: "Decryption key",
      placeholder: "Enter the key provided by the sender",
      description: "Ask the sender for the decryption key through a separate channel. The key is used only in this browser.",
      required: "Enter a decryption key.",
      error: "That decryption key could not unlock this secret. Check the key and try again.",
      show: "Show decryption key",
      hide: "Hide decryption key",
      change: "Enter a different key",
      submit: "Unlock secret"
    },
    credentials: {
      title: "Unlock Secret",
      description: "Enter the decryption key and password provided by the sender. Both are used only in this browser.",
      submit: "Unlock secret"
    },
    legacyKey: {
      warning: "This link was created with an outdated client that puts the decryption key in the query string. Ask the sender to upgrade so future links keep the key out of server logs."
    },
    content: {
      fileShared: "A file has been shared with you",
      downloadFile: "Download File",
      hideContent: "Hide content",
      showContent: "Show content",
      copyToClipboard: "Copy to clipboard",
      copiedToClipboard: "Secret copied to clipboard",
      clickToReveal: "Click the eye icon above to reveal the secret",
      passwordProtected: "This secret is password protected. Click to enter password.",
      ariaLabel: "Secret content"
    },
    info: {
      burnedAfterReading: "This secret has been permanently deleted. Once you leave this page, it cannot be viewed again.",
      expiresIn: "Expires {{time}}"
    },
    errors: {
      notFound: "not found",
      unexpectedStatus: "unexpected status code {{code}}"
    },
    actions: {
      viewSecret: "View Secret"
    }
  },
  about: {
    title: "About",
    what: {
      description: "crypt.fyi is a zero-knowledge way to share sensitive information \u2014 passwords, API keys, files \u2014 with a link. Encryption happens in your browser, our servers never see the plaintext, and secrets can vanish after they are read.",
      traditionalTitle: "Why not just email or text it?",
      traditionalDescription: "Email, SMS, Slack, and chat keep copies: in inboxes, message history, carrier logs, and every synced device. Once you paste a password into those channels, you lose control over who can find it later. crypt.fyi is for a deliberate, one-time handoff \u2014 share the link, optionally add a password or IP allow-list, and let the secret disappear when it is done."
    },
    letter: {
      title: "A note from the author",
      p1: "I built crypt.fyi because the tools I already reached for kept falling short when I needed to share something sensitive \u2014 especially with someone outside my password manager \u2014 with controls that matched how I actually work.",
      p2: "Incumbents often missed the UX details that matter day to day: expansive restrictions like IP allow-lists, read counts, burn after failed attempts, and webhooks; a CLI and browser extension; saved and pre-fill configuration. Technically, some were also behind on modern encryption standards, or lacked true atomic read-and-burn so a secret could not be race-conditioned into multiple reads.",
      p3: "So I built something new: open source, self-hostable, and opinionated about the cryptography and controls I wanted for myself.",
      signOff: "\u2014 Dillon"
    },
    openSource: {
      description: "crypt.fyi is open source and auditable. You can review the implementation, self-host it, or contribute on"
    },
    technical: {
      prompt: "Want to get more technical?",
      specLink: "Read the protocol specification"
    }
  },
  privacy: {
    title: "Privacy Policy",
    intro: "At crypt.fyi, we take your privacy seriously. This Privacy Policy explains how we handle your information when you use our zero-knowledge, end-to-end encrypted secret sharing platform.",
    doNotCollect: {
      title: "Information We Don't Collect",
      description: "Due to our zero-knowledge architecture, we are technically unable to access:",
      items: {
        secrets: "Your unencrypted secrets or files",
        keys: "Encryption keys or passwords",
        urls: "URL fragments containing decryption information",
        content: "The content of your encrypted data",
        recipients: "Information about the recipients of your secrets"
      }
    },
    collect: {
      title: "Information We Do Collect",
      description: "We collect and store only the minimum required information:",
      items: {
        encrypted: "Encrypted data (which we cannot decrypt)",
        hashes: "Key verification hashes (used to verify access rights without knowing the actual keys)",
        metadata: "Basic request metadata (IP addresses, timestamps) for rate limiting and abuse prevention",
        webhooks: "Optional webhook URLs if provided (for notifications about secret access and deletion)"
      },
      note: "All stored data is automatically deleted after expiration or when accessed (if burn after reading is enabled)."
    },
    usage: {
      title: "How We Use Information",
      description: "We use the collected information solely for:",
      items: {
        transmission: "Facilitating the secure transmission of your encrypted secrets",
        rateLimits: "Enforcing rate limits to prevent abuse",
        ipControl: "Implementing IP/CIDR allow-listing when configured",
        notifications: "Sending webhook notifications when enabled",
        security: "Maintaining system security and preventing unauthorized access"
      }
    },
    security: {
      title: "Data Storage and Security",
      description: "Our security measures include:",
      items: {
        encryption: "All encryption/decryption occurs in your browser using ML-KEM post-quantum encryption",
        csp: "Strict Content Security Policy (CSP) to prevent XSS attacks",
        tls: "TLS encryption for all API communications",
        expiration: "Automatic data expiration with configurable Time-To-Live (TTL)",
        deletion: "Secure deletion of data after access or expiration",
        storage: "No persistent storage of sensitive information"
      }
    },
    thirdParty: {
      title: "Third-Party Services",
      description: "We do not share any information with third parties except in the following cases:",
      items: {
        webhooks: "When you enable webhooks, we send notifications to the URLs you provide about secret access and deletion",
        infrastructure: "Infrastructure providers that host our services (who only see encrypted data they cannot decrypt)"
      }
    },
    rights: {
      title: "Your Rights and Choices",
      description: "You have control over your data:",
      items: {
        expiration: "Choose custom expiration times for your secrets",
        burn: "Enable burn after reading for immediate deletion after access",
        password: "Add password protection for additional security",
        ip: "Configure IP restrictions to control access",
        readLimits: "Set read count limits"
      }
    },
    changes: {
      title: "Changes to This Policy",
      description: "We may update this Privacy Policy from time to time. We will notify users of any material changes by posting the new Privacy Policy on this page. You are advised to review this Privacy Policy periodically for any changes."
    },
    contact: {
      title: "Contact Us",
      description: "If you have any questions about this Privacy Policy, you can contact us through our"
    }
  }
};

// src/i18n/locales/es.ts
var es = {
  common: {
    createSecret: "Crear Secreto",
    starOnGithub: "Estrella en GitHub",
    features: "Caracter\xEDsticas",
    learnMore: "M\xE1s Informaci\xF3n",
    loading: "Cargando...",
    create: "Crear",
    delete: "Eliminar",
    copy: "Copiar",
    share: "Compartir",
    download: "Descargar",
    success: "\xA1\xC9xito!",
    error: "Error",
    confirm: "Confirmar",
    requestNewLanguage: "Solicitar nuevo o corregir traducciones",
    footer: {
      tagline: "Construido con seguridad y privacidad en mente, porque la ignorancia puede ser una bendici\xF3n"
    },
    time: {
      minute_one: "{{count}} minuto",
      minute_other: "{{count}} minutos",
      hour_one: "{{count}} hora",
      hour_other: "{{count}} horas",
      day_one: "{{count}} d\xEDa",
      day_other: "{{count}} d\xEDas"
    }
  },
  landing: {
    title: "Compartir Secretos de Conocimiento Cero",
    subtitle: "Cifra en tu navegador. Comparte una vez. Desaparece para siempre. Cifrado post-cu\xE1ntico de extremo a extremo \u2014 nuestros servidores nunca ven tus secretos.",
    pillars: {
      title: "Dise\xF1ado para la confianza",
      zeroKnowledge: {
        title: "Verdadero conocimiento cero",
        description: "Las claves de cifrado nunca salen de tu dispositivo. El descifrado ocurre solo en el navegador del destinatario \u2014 el servidor nunca ve texto plano."
      },
      ephemeral: {
        title: "Ef\xEDmero por defecto",
        description: "Destrucci\xF3n tras la lectura, TTL personalizados y l\xEDmites de lectura para que los datos sensibles no permanezcan m\xE1s de lo necesario."
      },
      defense: {
        title: "Defensa en profundidad",
        description: "Cifrado post-cu\xE1ntico ML-KEM, CSP estricta, limitaci\xF3n de velocidad y listas de IP opcionales \u2014 protecci\xF3n en capas, no una sola casilla."
      },
      open: {
        title: "Abierto y auditable",
        description: "Totalmente de c\xF3digo abierto y pensado para autoalojamiento. Revisa el c\xF3digo, ejecuta tu propia instancia o contribuye."
      }
    },
    alsoIncludes: {
      label: "Tambi\xE9n incluye",
      password: "protecci\xF3n con contrase\xF1a",
      files: "compartir archivos",
      webhooks: "webhooks",
      qrCode: "c\xF3digos QR",
      ipControl: "listas de IP",
      readLimits: "l\xEDmites de lectura"
    },
    selfHost: {
      title: "Ejecuta tu propia instancia",
      description: "Despliega el mismo stack de conocimiento cero en tu infraestructura \u2014 un clic en Railway, o Docker Compose en cualquier lugar.",
      deployOnRailway: "Desplegar en Railway",
      dockerCompose: "Docker Compose"
    },
    ecosystem: {
      cli: "CLI",
      chromeExtension: "Extensi\xF3n de Chrome",
      github: "GitHub"
    },
    steps: {
      encrypt: {
        title: "1. Cifrar",
        description: "Tu secreto se cifra en tu navegador antes de ser almacenado"
      },
      share: {
        title: "2. Compartir",
        description: "Env\xEDa el enlace seguro a tu destinatario"
      },
      autoDelete: {
        title: "3. Auto-Eliminaci\xF3n",
        description: "El secreto se elimina permanentemente despu\xE9s de ser visto"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Contenido secreto",
        placeholder: "Ingresa tu contenido secreto aqu\xED...",
        fileHint: "agrega un archivo arrastr\xE1ndolo o haciendo clic aqu\xED (m\xE1x 1 MB)",
        fileSelected: "Archivo seleccionado: {{name}} ({{size}} KB)",
        dropFile: "Suelta el archivo aqu\xED",
        invalidFileType: "Tipo de archivo no v\xE1lido"
      },
      password: {
        label: "Contrase\xF1a",
        placeholder: "Opcional (pero recomendado)"
      },
      ttl: {
        label: "Tiempo de vida",
        placeholder: "Selecciona el tiempo de expiraci\xF3n"
      },
      burn: {
        label: "Destruir despu\xE9s de leer",
        description: "Garantiza que el secreto solo se pueda leer una vez \u2014 por la primera persona que abra el enlace"
      },
      advanced: {
        toggle: "configuraci\xF3n avanzada",
        ip: {
          label: "Lista de permitidos IP/CIDR",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Restringir el acceso a direcciones IP espec\xEDficas o bloques CIDR (separados por comas)"
        },
        readCount: {
          label: "Contador de lecturas",
          description: "N\xFAmero m\xE1ximo de veces que se puede leer el secreto"
        },
        failedAttempts: {
          label: "Eliminar despu\xE9s de intentos fallidos",
          description: "Eliminar autom\xE1ticamente el secreto despu\xE9s de varios intentos fallidos de acceso"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "URL del webhook a llamar cuando el secreto es le\xEDdo, destruido o falla al leer (configurable, si se establece)",
          read: "Lectura",
          burn: "Destrucci\xF3n",
          failureToReadPK: "Fallo (contrase\xF1a o clave)",
          failureToReadIP: "Fallo (IP o CIDR)",
          nameLabel: "Nombre",
          namePlaceholder: "Ingrese un nombre para este webhook",
          nameDescription: "Un nombre para identificar este webhook en las notificaciones"
        }
      }
    },
    success: {
      title: "\xA1Secreto Creado!",
      description: {
        main: "Tu secreto ha sido creado. Elige una opci\xF3n para compartirlo.",
        password: "Env\xEDa la contrase\xF1a por un canal separado.",
        separateKey: "Para enviarlos por separado, comparte la URL sin clave y la clave de descifrado por canales diferentes."
      },
      combinedUrl: "URL completa (incluye la clave)",
      keylessUrl: "URL sin clave",
      decryptionKey: "Clave de descifrado",
      urlCopied: "URL copiada al portapapeles",
      keyCopied: "Clave de descifrado copiada al portapapeles",
      secretDeleted: "Secreto eliminado",
      qrDownloaded: "C\xF3digo QR descargado",
      qrDownloadFailed: "Error al descargar el c\xF3digo QR: {{error}}",
      qrCode: {
        title: "C\xF3digo QR de la URL del Secreto",
        description: "Descarga y comparte el c\xF3digo QR de la URL del secreto"
      },
      actions: {
        showUrl: "Mostrar la URL del secreto",
        hideUrl: "Ocultar la URL del secreto",
        showKey: "Mostrar la clave de descifrado",
        hideKey: "Ocultar la clave de descifrado",
        shareUrl: "Compartir la URL del secreto",
        shareKey: "Compartir la clave de descifrado",
        copyUrl: "Copiar la URL del secreto",
        copyKey: "Copiar la clave de descifrado",
        showQr: "Mostrar el c\xF3digo QR de la URL completa"
      },
      createAnother: "Crear Otro",
      deleteSecret: "Eliminar Secreto",
      info: {
        expires: "Expira en: {{time}}",
        burn: "El secreto se eliminar\xE1 despu\xE9s de ser visto",
        passwordProtected: "Protegido con contrase\xF1a",
        ipRestrictions: "Restricci\xF3n(es) de IP: {{ips}}",
        readCount: "N\xFAmero de lecturas: {{count}}",
        webhook: "Webhook configurado para: {{events}} ({{url}})",
        failureCount: "El secreto se eliminar\xE1 despu\xE9s de {{count}} intentos fallidos"
      }
    },
    errors: {
      contentRequired: "El contenido es requerido",
      readCountWithBurn: "El contador de lecturas no se puede usar con destruir despu\xE9s de leer",
      tooManyIps: "Demasiadas restricciones de IP (m\xE1ximo {{max}})",
      invalidIp: "Direcci\xF3n IP o bloque CIDR inv\xE1lido: {{ip}}",
      uploadFailed: "Error al procesar el contenido cargado: {{error}}",
      deleteFailed: "Error al eliminar el secreto: {{error}}",
      secretNotFound: "secreto no encontrado",
      unexpectedStatus: "c\xF3digo de estado inesperado {{code}}",
      webhookConfigInvalid: "La configuraci\xF3n del webhook no es v\xE1lida: se requiere al menos un tipo de evento de webhook",
      fileSizeExceeded: "El archivo es demasiado grande. El tama\xF1o m\xE1ximo es {{max}}.",
      fileReadError: "Error al leer el archivo",
      fileReadAborted: "La lectura del archivo fue interrumpida"
    }
  },
  view: {
    notFound: {
      title: "Secreto No Encontrado",
      description: "Este secreto puede haber expirado o sido eliminado.",
      createNew: "Crear Nuevo Secreto"
    },
    invalidLink: {
      title: "Este enlace no es v\xE1lido",
      description: "El enlace est\xE1 incompleto o fue alterado durante el env\xEDo, por lo que el secreto no se puede descifrar. Pide al remitente que comparta de nuevo el enlace completo.",
      createNew: "Crear Nuevo Secreto"
    },
    connectionError: {
      title: "No se pudo conectar con el servidor",
      description: "El secreto puede seguir existiendo. Revisa tu conexi\xF3n e int\xE9ntalo de nuevo.",
      tryAgain: "Intentar de nuevo"
    },
    rateLimit: {
      title: "Demasiadas solicitudes",
      description: "Has realizado demasiadas solicitudes. Espera un momento e int\xE9ntalo de nuevo.",
      tryAgain: "Intentar de nuevo"
    },
    password: {
      title: "Ingresar Contrase\xF1a",
      label: "Contrase\xF1a",
      placeholder: "Ingresa la contrase\xF1a",
      description: "Este secreto est\xE1 protegido con una contrase\xF1a - solic\xEDtala al remitente",
      error: "La clave de descifrado o la contrase\xF1a es incorrecta",
      required: "Ingresa una contrase\xF1a.",
      show: "Mostrar la contrase\xF1a",
      hide: "Ocultar la contrase\xF1a"
    },
    key: {
      title: "Ingresa la clave de descifrado",
      label: "Clave de descifrado",
      placeholder: "Ingresa la clave proporcionada por el remitente",
      description: "Pide al remitente la clave de descifrado por un canal separado. La clave se usa solo en este navegador.",
      required: "Ingresa una clave de descifrado.",
      error: "Esa clave de descifrado no pudo desbloquear el secreto. Comprueba la clave e int\xE9ntalo de nuevo.",
      show: "Mostrar la clave de descifrado",
      hide: "Ocultar la clave de descifrado",
      change: "Ingresar otra clave",
      submit: "Desbloquear secreto"
    },
    credentials: {
      title: "Desbloquear secreto",
      description: "Ingresa la clave de descifrado y la contrase\xF1a proporcionadas por el remitente. Ambas se usan solo en este navegador.",
      submit: "Desbloquear secreto"
    },
    legacyKey: {
      warning: "Este enlace se cre\xF3 con un cliente desactualizado que pone la clave de descifrado en la cadena de consulta. Pide al remitente que actualice para que los enlaces futuros mantengan la clave fuera de los registros del servidor."
    },
    content: {
      fileShared: "Se ha compartido un archivo contigo",
      downloadFile: "Descargar Archivo",
      hideContent: "Ocultar contenido",
      showContent: "Mostrar contenido",
      copyToClipboard: "Copiar al portapapeles",
      copiedToClipboard: "Secreto copiado al portapapeles",
      clickToReveal: "Haz clic en el \xEDcono del ojo arriba para revelar el secreto",
      passwordProtected: "Este secreto est\xE1 protegido con contrase\xF1a. Haz clic para ingresar la contrase\xF1a.",
      ariaLabel: "Contenido secreto"
    },
    info: {
      burnedAfterReading: "Este secreto fue eliminado despu\xE9s de tu visualizaci\xF3n y ya no estar\xE1 disponible al salir de la p\xE1gina.",
      expiresIn: "Expira {{time}}"
    },
    errors: {
      notFound: "no encontrado",
      unexpectedStatus: "c\xF3digo de estado inesperado {{code}}"
    },
    actions: {
      viewSecret: "Ver Secreto"
    }
  },
  about: {
    title: "Acerca de",
    what: {
      description: "crypt.fyi es una forma de conocimiento cero de compartir informaci\xF3n sensible \u2014 contrase\xF1as, claves API, archivos \u2014 con un enlace. El cifrado ocurre en tu navegador, nuestros servidores nunca ven el texto plano, y los secretos pueden desaparecer despu\xE9s de ser le\xEDdos.",
      traditionalTitle: "\xBFPor qu\xE9 no enviarlo por correo o mensaje?",
      traditionalDescription: "El correo, SMS, Slack y el chat conservan copias: en bandejas de entrada, historiales, registros de operadores y en cada dispositivo sincronizado. Una vez pegas una contrase\xF1a en esos canales, pierdes el control sobre qui\xE9n puede encontrarla despu\xE9s. crypt.fyi es para una entrega deliberada y de un solo uso \u2014 comparte el enlace, opcionalmente a\xF1ade una contrase\xF1a o lista de IP, y deja que el secreto desaparezca cuando termine."
    },
    letter: {
      title: "Una nota del autor",
      p1: "Constru\xED crypt.fyi porque las herramientas a las que ya recurr\xEDa se quedaban cortas cuando necesitaba compartir algo sensible \u2014 especialmente con alguien fuera de mi gestor de contrase\xF1as \u2014 con controles que encajaran con c\xF3mo trabajo de verdad.",
      p2: "Los incumbentes a menudo carec\xEDan de los detalles de UX que importan d\xEDa a d\xEDa: restricciones amplias como listas de IP, l\xEDmites de lectura, destrucci\xF3n tras intentos fallidos y webhooks; una CLI y extensi\xF3n de navegador; configuraci\xF3n guardada y rellenada de antemano. T\xE9cnicamente, algunos tambi\xE9n iban por detr\xE1s en est\xE1ndares modernos de cifrado, o carec\xEDan de un verdadero read-and-burn at\xF3mico para que un secreto no pudiera leerse varias veces por una condici\xF3n de carrera.",
      p3: "As\xED que constru\xED algo nuevo: de c\xF3digo abierto, autoalojable y con opiniones claras sobre la criptograf\xEDa y los controles que yo mismo quer\xEDa.",
      signOff: "\u2014 Dillon"
    },
    openSource: {
      description: "crypt.fyi es de c\xF3digo abierto y auditable. Puedes revisar la implementaci\xF3n, autoalojarlo o contribuir en"
    },
    technical: {
      prompt: "\xBFQuieres ir m\xE1s a fondo?",
      specLink: "Lee la especificaci\xF3n del protocolo"
    }
  },
  privacy: {
    title: "Pol\xEDtica de Privacidad",
    intro: "En crypt.fyi, nos tomamos en serio tu privacidad. Esta Pol\xEDtica de Privacidad explica c\xF3mo manejamos tu informaci\xF3n cuando utilizas nuestra plataforma de intercambio de secretos con conocimiento cero y cifrado de extremo a extremo.",
    doNotCollect: {
      title: "Informaci\xF3n que No Recopilamos",
      description: "Debido a nuestra arquitectura de conocimiento cero, somos t\xE9cnicamente incapaces de acceder a:",
      items: {
        secrets: "Tus secretos o archivos sin cifrar",
        keys: "Claves de cifrado o contrase\xF1as",
        urls: "Fragmentos de URL que contienen informaci\xF3n de descifrado",
        content: "El contenido de tus datos cifrados",
        recipients: "Informaci\xF3n sobre los destinatarios de tus secretos"
      }
    },
    collect: {
      title: "Informaci\xF3n que Recopilamos",
      description: "Solo recopilamos y almacenamos la informaci\xF3n m\xEDnima necesaria:",
      items: {
        encrypted: "Datos cifrados (que no podemos descifrar)",
        hashes: "Hashes de verificaci\xF3n de claves (utilizados para verificar derechos de acceso sin conocer las claves reales)",
        metadata: "Metadatos b\xE1sicos de solicitudes (direcciones IP, marcas de tiempo) para l\xEDmites de tasa y prevenci\xF3n de abusos",
        webhooks: "URLs de webhook si se proporcionan (para notificaciones de acceso y eliminaci\xF3n de secretos)"
      },
      note: "Todos los datos almacenados se eliminan autom\xE1ticamente despu\xE9s de su vencimiento o al acceder (si est\xE1 activada la destrucci\xF3n despu\xE9s de lectura)."
    },
    usage: {
      title: "C\xF3mo Utilizamos la Informaci\xF3n",
      description: "Utilizamos la informaci\xF3n recopilada \xFAnicamente para:",
      items: {
        transmission: "Facilitar la transmisi\xF3n segura de tus secretos cifrados",
        rateLimits: "Aplicar l\xEDmites de tasa para prevenir abusos",
        ipControl: "Implementar lista blanca de IP/CIDR cuando est\xE1 configurada",
        notifications: "Enviar notificaciones webhook cuando est\xE1n activadas",
        security: "Mantener la seguridad del sistema y prevenir accesos no autorizados"
      }
    },
    security: {
      title: "Almacenamiento y Seguridad de Datos",
      description: "Nuestras medidas de seguridad incluyen:",
      items: {
        encryption: "Todo el cifrado/descifrado ocurre en tu navegador usando ML-KEM post-cu\xE1ntico",
        csp: "Pol\xEDtica de Seguridad de Contenido (CSP) estricta para prevenir ataques XSS",
        tls: "Cifrado TLS para todas las comunicaciones API",
        expiration: "Vencimiento autom\xE1tico de datos con tiempo de vida configurable (TTL)",
        deletion: "Eliminaci\xF3n segura de datos despu\xE9s del acceso o vencimiento",
        storage: "Sin almacenamiento persistente de informaci\xF3n sensible"
      }
    },
    thirdParty: {
      title: "Servicios de Terceros",
      description: "No compartimos informaci\xF3n con terceros excepto en los siguientes casos:",
      items: {
        webhooks: "Cuando activas los webhooks, enviamos notificaciones a las URLs que proporcionas sobre el acceso y eliminaci\xF3n de secretos",
        infrastructure: "Proveedores de infraestructura que alojan nuestros servicios (que solo ven datos cifrados que no pueden descifrar)"
      }
    },
    rights: {
      title: "Tus Derechos y Opciones",
      description: "Tienes control sobre tus datos:",
      items: {
        expiration: "Elegir tiempos de vencimiento personalizados para tus secretos",
        burn: "Activar destrucci\xF3n despu\xE9s de lectura para eliminaci\xF3n inmediata despu\xE9s del acceso",
        password: "Agregar protecci\xF3n con contrase\xF1a para seguridad adicional",
        ip: "Configurar restricciones de IP para controlar el acceso",
        readLimits: "Establecer l\xEDmites de lectura"
      }
    },
    changes: {
      title: "Cambios en esta Pol\xEDtica",
      description: "Podemos actualizar esta Pol\xEDtica de Privacidad de vez en cuando. Informaremos a los usuarios sobre cambios significativos publicando la nueva Pol\xEDtica de Privacidad en esta p\xE1gina. Te recomendamos revisar peri\xF3dicamente esta Pol\xEDtica de Privacidad para cualquier cambio."
    },
    contact: {
      title: "Cont\xE1ctanos",
      description: "Si tienes preguntas sobre esta Pol\xEDtica de Privacidad, puedes contactarnos a trav\xE9s de nuestra"
    }
  }
};

// src/i18n/locales/de.ts
var de = {
  common: {
    createSecret: "Geheimnis erstellen",
    starOnGithub: "Auf GitHub markieren",
    features: "Funktionen",
    learnMore: "Mehr erfahren",
    loading: "L\xE4dt...",
    create: "Erstellen",
    delete: "L\xF6schen",
    copy: "Kopieren",
    share: "Teilen",
    download: "Herunterladen",
    success: "Erfolg!",
    error: "Fehler",
    confirm: "Best\xE4tigen",
    requestNewLanguage: "Neue Sprache anfordern oder \xDCbersetzungen korrigieren",
    footer: {
      tagline: "Mit Sicherheit und Privatsph\xE4re im Fokus, weil Unwissenheit ein Segen sein kann"
    },
    time: {
      minute_one: "{{count}} Minute",
      minute_other: "{{count}} Minuten",
      hour_one: "{{count}} Stunde",
      hour_other: "{{count}} Stunden",
      day_one: "{{count}} Tag",
      day_other: "{{count}} Tage"
    }
  },
  landing: {
    title: "Zero-Knowledge Geheimnis-Sharing",
    subtitle: "Im Browser verschl\xFCsseln. Einmal teilen. F\xFCr immer weg. Post-Quanten Ende-zu-Ende-Verschl\xFCsselung \u2014 unsere Server sehen Ihre Geheimnisse nie.",
    pillars: {
      title: "F\xFCr Vertrauen gebaut",
      zeroKnowledge: {
        title: "Echtes Zero-Knowledge",
        description: "Verschl\xFCsselungsschl\xFCssel verlassen Ihr Ger\xE4t nie. Die Entschl\xFCsselung erfolgt nur im Browser des Empf\xE4ngers \u2014 der Server sieht niemals Klartext."
      },
      ephemeral: {
        title: "Standardm\xE4\xDFig fl\xFCchtig",
        description: "L\xF6schen nach dem Lesen, individuelle TTLs und Leselimits, damit sensible Daten nicht l\xE4nger als n\xF6tig bestehen."
      },
      defense: {
        title: "Mehrschichtiger Schutz",
        description: "ML-KEM Post-Quanten-Verschl\xFCsselung, strikte CSP, Ratenbegrenzung und optionale IP-Allow-Lists \u2014 Schutz in Schichten, nicht als einzelne Checkbox."
      },
      open: {
        title: "Offen und pr\xFCfbar",
        description: "Vollst\xE4ndig Open Source und f\xFCr Self-Hosting ausgelegt. Code pr\xFCfen, eigene Instanz betreiben oder mitwirken."
      }
    },
    alsoIncludes: {
      label: "Au\xDFerdem",
      password: "Passwortschutz",
      files: "Datei-Sharing",
      webhooks: "Webhooks",
      qrCode: "QR-Codes",
      ipControl: "IP-Allow-Lists",
      readLimits: "Leselimits"
    },
    selfHost: {
      title: "Eigene Instanz betreiben",
      description: "Denselben Zero-Knowledge-Stack auf Ihrer Infrastruktur bereitstellen \u2014 mit einem Klick auf Railway oder per Docker Compose \xFCberall.",
      deployOnRailway: "Auf Railway bereitstellen",
      dockerCompose: "Docker Compose"
    },
    ecosystem: {
      cli: "CLI",
      chromeExtension: "Chrome-Erweiterung",
      github: "GitHub"
    },
    steps: {
      encrypt: {
        title: "1. Verschl\xFCsseln",
        description: "Ihr Geheimnis wird in Ihrem Browser verschl\xFCsselt, bevor es gespeichert wird"
      },
      share: {
        title: "2. Teilen",
        description: "Senden Sie den sicheren Link an Ihren gew\xFCnschten Empf\xE4nger"
      },
      autoDelete: {
        title: "3. Automatisch l\xF6schen",
        description: "Das Geheimnis wird nach dem Anzeigen permanent gel\xF6scht"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Geheimer Inhalt",
        placeholder: "Geben Sie hier Ihren geheimen Inhalt ein...",
        fileHint: "Datei per Drag & Drop hinzuf\xFCgen oder hier klicken (max. 1 MB)",
        fileSelected: "Datei ausgew\xE4hlt: {{name}} ({{size}} KB)",
        dropFile: "Datei hier ablegen",
        invalidFileType: "Ung\xFCltiger Dateityp"
      },
      password: {
        label: "Passwort",
        placeholder: "Optional (aber empfohlen)"
      },
      ttl: {
        label: "G\xFCltigkeitsdauer",
        placeholder: "Ablaufzeit ausw\xE4hlen"
      },
      burn: {
        label: "Nach dem Lesen l\xF6schen",
        description: "Garantiert, dass das Geheimnis nur einmal gelesen werden kann \u2014 von der ersten Person, die den Link \xF6ffnet"
      },
      advanced: {
        toggle: "erweiterte Konfiguration",
        ip: {
          label: "IP/CIDR Positivliste",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Zugriff auf bestimmte IP-Adressen oder CIDR-Bl\xF6cke beschr\xE4nken (durch Kommas getrennt)"
        },
        readCount: {
          label: "Lesez\xE4hler",
          description: "Maximale Anzahl der Abrufe des Geheimnisses"
        },
        failedAttempts: {
          label: "Nach fehlgeschlagenen Versuchen l\xF6schen",
          description: "Geheimnis automatisch nach einer Anzahl fehlgeschlagener Zugriffsversuche l\xF6schen"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "Webhook-URL, die aufgerufen wird, wenn das Geheimnis gelesen, gel\xF6scht oder nicht gelesen werden kann (konfigurierbar, falls gesetzt)",
          read: "Lesen",
          burn: "L\xF6schen",
          failureToReadPK: "Fehler (Passwort oder Schl\xFCssel)",
          failureToReadIP: "Fehler (IP oder CIDR)",
          nameLabel: "Name",
          namePlaceholder: "Geben Sie einen Namen f\xFCr diesen Webhook ein",
          nameDescription: "Ein Name zur Identifizierung dieses Webhooks in Benachrichtigungen"
        }
      }
    },
    success: {
      title: "Geheimnis erstellt!",
      description: {
        main: "Ihr Geheimnis wurde erstellt. W\xE4hlen Sie unten eine Freigabeoption aus.",
        password: "Senden Sie das Passwort \xFCber einen separaten Kanal.",
        separateKey: "Senden Sie f\xFCr eine getrennte \xDCbermittlung die URL ohne Schl\xFCssel und den Entschl\xFCsselungsschl\xFCssel \xFCber verschiedene Kan\xE4le."
      },
      combinedUrl: "Komplette URL (mit Schl\xFCssel)",
      keylessUrl: "URL ohne Schl\xFCssel",
      decryptionKey: "Entschl\xFCsselungsschl\xFCssel",
      urlCopied: "URL in die Zwischenablage kopiert",
      keyCopied: "Entschl\xFCsselungsschl\xFCssel in die Zwischenablage kopiert",
      secretDeleted: "Geheimnis gel\xF6scht",
      qrDownloaded: "QR-Code heruntergeladen",
      qrDownloadFailed: "QR-Code konnte nicht heruntergeladen werden: {{error}}",
      qrCode: {
        title: "Geheimnis-URL QR-Code",
        description: "Laden Sie den QR-Code der Geheimnis-URL herunter und teilen Sie ihn"
      },
      actions: {
        showUrl: "Geheimnis-URL anzeigen",
        hideUrl: "Geheimnis-URL ausblenden",
        showKey: "Entschl\xFCsselungsschl\xFCssel anzeigen",
        hideKey: "Entschl\xFCsselungsschl\xFCssel ausblenden",
        shareUrl: "Geheimnis-URL teilen",
        shareKey: "Entschl\xFCsselungsschl\xFCssel teilen",
        copyUrl: "Geheimnis-URL kopieren",
        copyKey: "Entschl\xFCsselungsschl\xFCssel kopieren",
        showQr: "QR-Code der kompletten URL anzeigen"
      },
      createAnother: "Weiteres erstellen",
      deleteSecret: "Geheimnis l\xF6schen",
      info: {
        expires: "L\xE4uft ab in: {{time}}",
        burn: "Geheimnis wird nach dem Lesen gel\xF6scht",
        passwordProtected: "Passwortgesch\xFCtzt",
        ipRestrictions: "IP-Beschr\xE4nkung(en): {{ips}}",
        readCount: "Leseanzahl: {{count}}",
        webhook: "Webhook konfiguriert f\xFCr: {{events}} ({{url}})",
        failureCount: "Geheimnis wird nach {{count}} fehlgeschlagenen Versuchen gel\xF6scht"
      }
    },
    errors: {
      contentRequired: "Inhalt ist erforderlich",
      readCountWithBurn: 'Lesez\xE4hler kann nicht mit "Nach dem Lesen l\xF6schen" kombiniert werden',
      tooManyIps: "Zu viele IP-Beschr\xE4nkungen (maximal {{max}})",
      invalidIp: "Ung\xFCltige IP-Adresse oder CIDR-Block: {{ip}}",
      uploadFailed: "Verarbeitung des Inhalts fehlgeschlagen: {{error}}",
      deleteFailed: "L\xF6schen des Geheimnisses fehlgeschlagen: {{error}}",
      secretNotFound: "Geheimnis nicht gefunden",
      unexpectedStatus: "Unerwarteter Statuscode {{code}}",
      webhookConfigInvalid: "Webhook-Konfiguration ist ung\xFCltig - mindestens ein Webhook-Ereignistyp ist erforderlich",
      fileSizeExceeded: "Datei ist zu gro\xDF. Maximale Gr\xF6\xDFe ist {{max}}.",
      fileReadError: "Fehler beim Lesen der Datei",
      fileReadAborted: "Dateilesen wurde abgebrochen"
    }
  },
  view: {
    notFound: {
      title: "Geheimnis nicht gefunden",
      description: "Dieses Geheimnis ist m\xF6glicherweise abgelaufen oder wurde gel\xF6scht.",
      createNew: "Neues Geheimnis erstellen"
    },
    invalidLink: {
      title: "Dieser Link ist ung\xFCltig",
      description: "Der Link ist unvollst\xE4ndig oder wurde bei der \xDCbertragung ver\xE4ndert, sodass das Geheimnis nicht entschl\xFCsselt werden kann. Bitten Sie den Absender, den vollst\xE4ndigen Link erneut zu senden.",
      createNew: "Neues Geheimnis erstellen"
    },
    connectionError: {
      title: "Server nicht erreichbar",
      description: "Das Geheimnis existiert m\xF6glicherweise noch. \xDCberpr\xFCfen Sie Ihre Verbindung und versuchen Sie es erneut.",
      tryAgain: "Erneut versuchen"
    },
    rateLimit: {
      title: "Zu viele Anfragen",
      description: "Sie haben zu viele Anfragen gestellt. Warten Sie einen Moment und versuchen Sie es erneut.",
      tryAgain: "Erneut versuchen"
    },
    password: {
      title: "Passwort eingeben",
      label: "Passwort",
      placeholder: "Geben Sie das Passwort ein",
      description: "Dieses Geheimnis ist mit einem Passwort gesch\xFCtzt - fragen Sie den Absender",
      error: "Falscher Entschl\xFCsselungsschl\xFCssel oder falsches Passwort",
      required: "Geben Sie ein Passwort ein.",
      show: "Passwort anzeigen",
      hide: "Passwort ausblenden"
    },
    key: {
      title: "Entschl\xFCsselungsschl\xFCssel eingeben",
      label: "Entschl\xFCsselungsschl\xFCssel",
      placeholder: "Vom Absender erhaltenen Schl\xFCssel eingeben",
      description: "Bitten Sie den Absender \xFCber einen separaten Kanal um den Entschl\xFCsselungsschl\xFCssel. Der Schl\xFCssel wird nur in diesem Browser verwendet.",
      required: "Geben Sie einen Entschl\xFCsselungsschl\xFCssel ein.",
      error: "Dieser Entschl\xFCsselungsschl\xFCssel konnte das Geheimnis nicht \xF6ffnen. Pr\xFCfen Sie den Schl\xFCssel und versuchen Sie es erneut.",
      show: "Entschl\xFCsselungsschl\xFCssel anzeigen",
      hide: "Entschl\xFCsselungsschl\xFCssel ausblenden",
      change: "Anderen Schl\xFCssel eingeben",
      submit: "Geheimnis entsperren"
    },
    credentials: {
      title: "Geheimnis entsperren",
      description: "Geben Sie den vom Absender erhaltenen Entschl\xFCsselungsschl\xFCssel und das Passwort ein. Beides wird nur in diesem Browser verwendet.",
      submit: "Geheimnis entsperren"
    },
    legacyKey: {
      warning: "Dieser Link wurde mit einem veralteten Client erstellt, der den Entschl\xFCsselungsschl\xFCssel in der Abfragezeichenfolge speichert. Bitten Sie den Absender um ein Upgrade, damit zuk\xFCnftige Links den Schl\xFCssel aus Serverprotokollen fernhalten."
    },
    content: {
      fileShared: "Eine Datei wurde mit Ihnen geteilt",
      downloadFile: "Datei herunterladen",
      hideContent: "Inhalt verbergen",
      showContent: "Inhalt anzeigen",
      copyToClipboard: "In die Zwischenablage kopieren",
      copiedToClipboard: "Geheimnis in die Zwischenablage kopiert",
      clickToReveal: "Klicken Sie auf das Augensymbol oben, um das Geheimnis anzuzeigen",
      passwordProtected: "Dieses Geheimnis ist passwortgesch\xFCtzt. Klicken Sie, um das Passwort einzugeben.",
      ariaLabel: "Geheimer Inhalt"
    },
    info: {
      burnedAfterReading: "Dieses Geheimnis wurde nach Ihrer Ansicht gel\xF6scht und ist nach Verlassen der Seite nicht mehr verf\xFCgbar.",
      expiresIn: "L\xE4uft ab in {{time}}"
    },
    errors: {
      notFound: "nicht gefunden",
      unexpectedStatus: "unerwarteter Statuscode {{code}}"
    },
    actions: {
      viewSecret: "Geheimnis anzeigen"
    }
  },
  about: {
    title: "\xDCber",
    what: {
      description: "crypt.fyi ist ein Zero-Knowledge-Weg, sensible Informationen \u2014 Passw\xF6rter, API-Schl\xFCssel, Dateien \u2014 per Link zu teilen. Die Verschl\xFCsselung erfolgt in Ihrem Browser, unsere Server sehen nie den Klartext, und Geheimnisse k\xF6nnen nach dem Lesen verschwinden.",
      traditionalTitle: "Warum nicht einfach per E-Mail oder SMS?",
      traditionalDescription: "E-Mail, SMS, Slack und Chat behalten Kopien: in Postf\xE4chern, Nachrichtenverl\xE4ufen, Anbieter-Logs und auf jedem synchronisierten Ger\xE4t. Sobald Sie ein Passwort in diese Kan\xE4le einf\xFCgen, verlieren Sie die Kontrolle dar\xFCber, wer es sp\xE4ter finden kann. crypt.fyi ist f\xFCr eine bewusste, einmalige \xDCbergabe \u2014 Link teilen, optional Passwort oder IP-Allow-List setzen, und das Geheimnis verschwinden lassen, wenn es erledigt ist."
    },
    letter: {
      title: "Eine Notiz vom Autor",
      p1: "Ich habe crypt.fyi gebaut, weil die Tools, nach denen ich bereits griff, immer wieder zu kurz kamen, wenn ich etwas Sensibles teilen musste \u2014 besonders mit jemandem au\xDFerhalb meines Passwort-Managers \u2014 mit Kontrollen, die zu meiner tats\xE4chlichen Arbeit passten.",
      p2: "Bestehende L\xF6sungen vermissten oft die UX-Details, die im Alltag z\xE4hlen: umfangreiche Einschr\xE4nkungen wie IP-Allow-Lists, Leselimits, L\xF6schen nach fehlgeschlagenen Versuchen und Webhooks; ein CLI und eine Browser-Erweiterung; gespeicherte und vorgef\xFCllte Konfiguration. Technisch lagen manche auch hinter modernen Verschl\xFCsselungsstandards zur\xFCck, oder ihnen fehlte echte atomare Read-and-Burn-Logik, sodass ein Geheimnis nicht per Race Condition mehrfach gelesen werden konnte.",
      p3: "Also habe ich etwas Neues gebaut: Open Source, self-hostbar und klar in den Kryptografie- und Kontrollentscheidungen, die ich f\xFCr mich selbst wollte.",
      signOff: "\u2014 Dillon"
    },
    openSource: {
      description: "crypt.fyi ist Open Source und pr\xFCfbar. Sie k\xF6nnen die Implementierung einsehen, selbst hosten oder mitwirken auf"
    },
    technical: {
      prompt: "Mehr technische Details?",
      specLink: "Zur Protokollspezifikation"
    }
  },
  privacy: {
    title: "Datenschutzerkl\xE4rung",
    intro: "Bei crypt.fyi nehmen wir Ihre Privatsph\xE4re ernst. Diese Datenschutzerkl\xE4rung erl\xE4utert, wie wir mit Ihren Informationen umgehen, wenn Sie unsere Zero-Knowledge, Ende-zu-Ende-verschl\xFCsselte Plattform f\xFCr den Austausch von Geheimnissen nutzen.",
    doNotCollect: {
      title: "Informationen, die wir nicht sammeln",
      description: "Aufgrund unserer Zero-Knowledge-Architektur sind wir technisch nicht in der Lage, auf Folgendes zuzugreifen:",
      items: {
        secrets: "Ihre unverschl\xFCsselten Geheimnisse oder Dateien",
        keys: "Verschl\xFCsselungsschl\xFCssel oder Passw\xF6rter",
        urls: "URL-Fragmente mit Entschl\xFCsselungsinformationen",
        content: "Den Inhalt Ihrer verschl\xFCsselten Daten",
        recipients: "Informationen \xFCber die Empf\xE4nger Ihrer Geheimnisse"
      }
    },
    collect: {
      title: "Informationen, die wir sammeln",
      description: "Wir sammeln und speichern nur die minimal erforderlichen Informationen:",
      items: {
        encrypted: "Verschl\xFCsselte Daten (die wir nicht entschl\xFCsseln k\xF6nnen)",
        hashes: "Schl\xFCssel-Verifizierungs-Hashes (verwendet zur \xDCberpr\xFCfung der Zugriffsrechte ohne die tats\xE4chlichen Schl\xFCssel zu kennen)",
        metadata: "Grundlegende Anfrage-Metadaten (IP-Adressen, Zeitstempel) f\xFCr Ratenbegrenzung und Missbrauchspr\xE4vention",
        webhooks: "Webhook-URLs, falls angegeben (f\xFCr Benachrichtigungen \xFCber Zugriff und L\xF6schung von Geheimnissen)"
      },
      note: 'Alle gespeicherten Daten werden nach Ablauf oder beim Zugriff automatisch gel\xF6scht (wenn "Nach dem Lesen l\xF6schen" aktiviert ist).'
    },
    usage: {
      title: "Wie wir Informationen verwenden",
      description: "Wir verwenden die gesammelten Informationen nur f\xFCr:",
      items: {
        transmission: "Erm\xF6glichung der sicheren \xDCbertragung Ihrer verschl\xFCsselten Geheimnisse",
        rateLimits: "Durchsetzung von Ratenbegrenzungen zur Verhinderung von Missbrauch",
        ipControl: "Implementierung von IP/CIDR-Whitelisting, wenn konfiguriert",
        notifications: "Senden von Webhook-Benachrichtigungen, wenn aktiviert",
        security: "Aufrechterhaltung der Systemsicherheit und Verhinderung unbefugter Zugriffe"
      }
    },
    security: {
      title: "Datenspeicherung und Sicherheit",
      description: "Unsere Sicherheitsma\xDFnahmen umfassen:",
      items: {
        encryption: "Alle Ver- und Entschl\xFCsselung erfolgt in Ihrem Browser unter Verwendung von ML-KEM Post-Quanten-Verschl\xFCsselung",
        csp: "Strikte Content Security Policy (CSP) zur Verhinderung von XSS-Angriffen",
        tls: "TLS-Verschl\xFCsselung f\xFCr alle API-Kommunikation",
        expiration: "Automatischer Datenablauf mit konfigurierbarer Lebensdauer (TTL)",
        deletion: "Sichere Datenl\xF6schung nach Zugriff oder Ablauf",
        storage: "Keine dauerhafte Speicherung sensibler Informationen"
      }
    },
    thirdParty: {
      title: "Drittanbieterdienste",
      description: "Wir teilen keine Informationen mit Dritten, au\xDFer in den folgenden F\xE4llen:",
      items: {
        webhooks: "Wenn Sie Webhooks aktivieren, senden wir Benachrichtigungen an die von Ihnen angegebenen URLs \xFCber Zugriff und L\xF6schung von Geheimnissen",
        infrastructure: "Infrastrukturanbieter, die unsere Dienste hosten (die nur verschl\xFCsselte Daten sehen, die sie nicht entschl\xFCsseln k\xF6nnen)"
      }
    },
    rights: {
      title: "Ihre Rechte und Wahlm\xF6glichkeiten",
      description: "Sie haben die Kontrolle \xFCber Ihre Daten:",
      items: {
        expiration: "W\xE4hlen Sie benutzerdefinierte Ablauffristen f\xFCr Ihre Geheimnisse",
        burn: 'Aktivieren Sie "Nach dem Lesen l\xF6schen" f\xFCr sofortige L\xF6schung nach dem Zugriff',
        password: "F\xFCgen Sie Passwortschutz f\xFCr zus\xE4tzliche Sicherheit hinzu",
        ip: "Konfigurieren Sie IP-Beschr\xE4nkungen zur Zugriffskontrolle",
        readLimits: "Legen Sie Lesebeschr\xE4nkungen fest"
      }
    },
    changes: {
      title: "\xC4nderungen dieser Richtlinie",
      description: "Wir k\xF6nnen diese Datenschutzerkl\xE4rung von Zeit zu Zeit aktualisieren. Wir werden die Benutzer \xFCber wesentliche \xC4nderungen informieren, indem wir die neue Datenschutzerkl\xE4rung auf dieser Seite ver\xF6ffentlichen. Wir empfehlen Ihnen, diese Datenschutzerkl\xE4rung regelm\xE4\xDFig auf \xC4nderungen zu \xFCberpr\xFCfen."
    },
    contact: {
      title: "Kontakt",
      description: "Wenn Sie Fragen zu dieser Datenschutzerkl\xE4rung haben, k\xF6nnen Sie uns \xFCber unsere"
    }
  }
};

// src/i18n/locales/fr.ts
var fr = {
  common: {
    createSecret: "Cr\xE9er un Secret",
    starOnGithub: "Star sur GitHub",
    features: "Fonctionnalit\xE9s",
    learnMore: "En savoir plus",
    loading: "Chargement...",
    create: "Cr\xE9er",
    delete: "Supprimer",
    copy: "Copier",
    share: "Partager",
    download: "T\xE9l\xE9charger",
    success: "Succ\xE8s !",
    error: "Erreur",
    confirm: "Confirmer",
    requestNewLanguage: "Demander nouvelle langue ou corriger traductions",
    footer: {
      tagline: "Construit avec la s\xE9curit\xE9 et la confidentialit\xE9 \xE0 l'esprit, parce que l'ignorance peut \xEAtre une b\xE9n\xE9diction"
    },
    time: {
      minute_one: "{{count}} minute",
      minute_other: "{{count}} minutes",
      hour_one: "{{count}} heure",
      hour_other: "{{count}} heures",
      day_one: "{{count}} jour",
      day_other: "{{count}} jours"
    }
  },
  landing: {
    title: "Partage de Secrets Zero-Knowledge",
    subtitle: "Chiffrez dans votre navigateur. Partagez une fois. Disparu pour toujours. Chiffrement post-quantique de bout en bout \u2014 nos serveurs ne voient jamais vos secrets.",
    pillars: {
      title: "Con\xE7u pour la confiance",
      zeroKnowledge: {
        title: "Vrai zero-knowledge",
        description: "Les cl\xE9s de chiffrement ne quittent jamais votre appareil. Le d\xE9chiffrement a lieu uniquement dans le navigateur du destinataire \u2014 le serveur ne voit jamais le texte en clair."
      },
      ephemeral: {
        title: "\xC9ph\xE9m\xE8re par d\xE9faut",
        description: "Destruction apr\xE8s lecture, TTL personnalis\xE9s et limites de lecture pour que les donn\xE9es sensibles ne persistent pas plus que n\xE9cessaire."
      },
      defense: {
        title: "D\xE9fense en profondeur",
        description: "Chiffrement post-quantique ML-KEM, CSP stricte, limitation de d\xE9bit et listes IP optionnelles \u2014 une protection en couches, pas une simple case \xE0 cocher."
      },
      open: {
        title: "Ouvert et auditable",
        description: "Enti\xE8rement open source et con\xE7u pour l'auto-h\xE9bergement. Inspectez le code, h\xE9bergez votre instance ou contribuez."
      }
    },
    alsoIncludes: {
      label: "Inclut aussi",
      password: "protection par mot de passe",
      files: "partage de fichiers",
      webhooks: "webhooks",
      qrCode: "codes QR",
      ipControl: "listes IP",
      readLimits: "limites de lecture"
    },
    selfHost: {
      title: "H\xE9bergez votre propre instance",
      description: "D\xE9ployez le m\xEAme stack zero-knowledge sur votre infrastructure \u2014 un clic sur Railway, ou Docker Compose partout.",
      deployOnRailway: "D\xE9ployer sur Railway",
      dockerCompose: "Docker Compose"
    },
    ecosystem: {
      cli: "CLI",
      chromeExtension: "Extension Chrome",
      github: "GitHub"
    },
    steps: {
      encrypt: {
        title: "1. Chiffrer",
        description: "Votre secret est chiffr\xE9 dans votre navigateur avant d'\xEAtre stock\xE9"
      },
      share: {
        title: "2. Partager",
        description: "Envoyez le lien s\xE9curis\xE9 \xE0 votre destinataire"
      },
      autoDelete: {
        title: "3. Auto-Suppression",
        description: "Le secret est d\xE9finitivement supprim\xE9 apr\xE8s avoir \xE9t\xE9 consult\xE9"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "Contenu secret",
        placeholder: "Saisissez votre contenu secret ici...",
        fileHint: "ajoutez un fichier en le d\xE9posant ou en cliquant ici (max 1 Mo)",
        fileSelected: "Fichier s\xE9lectionn\xE9 : {{name}} ({{size}} Ko)",
        dropFile: "D\xE9posez le fichier ici",
        invalidFileType: "Type de fichier invalide"
      },
      password: {
        label: "Mot de passe",
        placeholder: "Optionnel (mais recommand\xE9)"
      },
      ttl: {
        label: "Dur\xE9e de vie",
        placeholder: "S\xE9lectionnez le d\xE9lai d'expiration"
      },
      burn: {
        label: "D\xE9truire apr\xE8s lecture",
        description: "Garantit que le secret ne peut \xEAtre lu qu'une seule fois \u2014 par la premi\xE8re personne qui ouvre le lien"
      },
      advanced: {
        toggle: "configuration avanc\xE9e",
        ip: {
          label: "Liste d'IP/CIDR autoris\xE9es",
          placeholder: "192.168.1.1, 10.0.0.0/24, etc.",
          description: "Restreindre l'acc\xE8s \xE0 des adresses IP sp\xE9cifiques ou des blocs CIDR (s\xE9par\xE9s par des virgules)"
        },
        readCount: {
          label: "Nombre de lectures",
          description: "Nombre maximum de fois que le secret peut \xEAtre lu"
        },
        failedAttempts: {
          label: "Supprimer apr\xE8s tentatives \xE9chou\xE9es",
          description: "Supprimer automatiquement le secret apr\xE8s un certain nombre de tentatives d'acc\xE8s \xE9chou\xE9es"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "URL du webhook \xE0 appeler lorsque le secret est lu, d\xE9truit ou \xE9choue \xE0 \xEAtre lu (configurable, si d\xE9fini)",
          read: "Lecture",
          burn: "Destruction",
          failureToReadPK: "\xC9chec (mot de passe ou cl\xE9)",
          failureToReadIP: "\xC9chec (IP ou CIDR)",
          nameLabel: "Nom",
          namePlaceholder: "Entrez un nom pour ce webhook",
          nameDescription: "Un nom pour identifier ce webhook dans les notifications"
        }
      }
    },
    success: {
      title: "Secret Cr\xE9\xE9 !",
      description: {
        main: "Votre secret a \xE9t\xE9 cr\xE9\xE9. Choisissez une option de partage ci-dessous.",
        password: "Envoyez le mot de passe par un canal s\xE9par\xE9.",
        separateKey: "Pour un envoi s\xE9par\xE9, transmettez l'URL sans cl\xE9 et la cl\xE9 de d\xE9chiffrement par des canaux diff\xE9rents."
      },
      combinedUrl: "URL compl\xE8te (avec la cl\xE9)",
      keylessUrl: "URL sans cl\xE9",
      decryptionKey: "Cl\xE9 de d\xE9chiffrement",
      urlCopied: "URL copi\xE9e dans le presse-papiers",
      keyCopied: "Cl\xE9 de d\xE9chiffrement copi\xE9e dans le presse-papiers",
      secretDeleted: "Secret supprim\xE9",
      qrDownloaded: "Code QR t\xE9l\xE9charg\xE9",
      qrDownloadFailed: "\xC9chec du t\xE9l\xE9chargement du code QR : {{error}}",
      qrCode: {
        title: "Code QR de l'URL du Secret",
        description: "T\xE9l\xE9chargez et partagez le code QR de l'URL du secret"
      },
      actions: {
        showUrl: "Afficher l'URL du secret",
        hideUrl: "Masquer l'URL du secret",
        showKey: "Afficher la cl\xE9 de d\xE9chiffrement",
        hideKey: "Masquer la cl\xE9 de d\xE9chiffrement",
        shareUrl: "Partager l'URL du secret",
        shareKey: "Partager la cl\xE9 de d\xE9chiffrement",
        copyUrl: "Copier l'URL du secret",
        copyKey: "Copier la cl\xE9 de d\xE9chiffrement",
        showQr: "Afficher le code QR de l'URL compl\xE8te"
      },
      createAnother: "Cr\xE9er un Autre",
      deleteSecret: "Supprimer le Secret",
      info: {
        expires: "Expire dans: {{time}}",
        burn: "Le secret sera supprim\xE9 apr\xE8s sa lecture",
        passwordProtected: "Prot\xE9g\xE9 par mot de passe",
        ipRestrictions: "Restriction(s) IP: {{ips}}",
        readCount: "Nombre de lectures: {{count}}",
        webhook: "Webhook configur\xE9 pour: {{events}} ({{url}})",
        failureCount: "Le secret sera supprim\xE9 apr\xE8s {{count}} tentatives \xE9chou\xE9es"
      }
    },
    errors: {
      contentRequired: "Le contenu est requis",
      readCountWithBurn: "Le nombre de lectures ne peut pas \xEAtre utilis\xE9 avec la destruction apr\xE8s lecture",
      tooManyIps: "Trop de restrictions IP (maximum {{max}})",
      invalidIp: "Adresse IP ou bloc CIDR invalide : {{ip}}",
      uploadFailed: "Erreur lors du traitement du contenu t\xE9l\xE9charg\xE9 : {{error}}",
      deleteFailed: "Erreur lors de la suppression du secret : {{error}}",
      secretNotFound: "secret non trouv\xE9",
      unexpectedStatus: "code de statut inattendu {{code}}",
      webhookConfigInvalid: "La configuration du webhook est invalide - au moins un type d'\xE9v\xE9nement webhook est requis",
      fileSizeExceeded: "Le fichier est trop volumineux. La taille maximale est {{max}}.",
      fileReadError: "\xC9chec de la lecture du fichier",
      fileReadAborted: "La lecture du fichier a \xE9t\xE9 interrompue"
    }
  },
  view: {
    notFound: {
      title: "Secret Non Trouv\xE9",
      description: "Ce secret a peut-\xEAtre expir\xE9 ou a \xE9t\xE9 supprim\xE9.",
      createNew: "Cr\xE9er un Nouveau Secret"
    },
    invalidLink: {
      title: "Ce lien est invalide",
      description: "Le lien est incomplet ou a \xE9t\xE9 modifi\xE9 pendant le transfert, le secret ne peut donc pas \xEAtre d\xE9chiffr\xE9. Demandez \xE0 l'exp\xE9diteur de partager \xE0 nouveau le lien complet.",
      createNew: "Cr\xE9er un Nouveau Secret"
    },
    connectionError: {
      title: "Impossible de joindre le serveur",
      description: "Le secret existe peut-\xEAtre encore. V\xE9rifiez votre connexion et r\xE9essayez.",
      tryAgain: "R\xE9essayer"
    },
    rateLimit: {
      title: "Trop de requ\xEAtes",
      description: "Vous avez effectu\xE9 trop de requ\xEAtes. Attendez un moment et r\xE9essayez.",
      tryAgain: "R\xE9essayer"
    },
    password: {
      title: "Saisir le Mot de Passe",
      label: "Mot de passe",
      placeholder: "Saisissez le mot de passe",
      description: "Ce secret est prot\xE9g\xE9 par un mot de passe - demandez-le \xE0 l'exp\xE9diteur",
      error: "Cl\xE9 de d\xE9chiffrement ou mot de passe incorrect",
      required: "Saisissez un mot de passe.",
      show: "Afficher le mot de passe",
      hide: "Masquer le mot de passe"
    },
    key: {
      title: "Saisir la cl\xE9 de d\xE9chiffrement",
      label: "Cl\xE9 de d\xE9chiffrement",
      placeholder: "Saisissez la cl\xE9 fournie par l'exp\xE9diteur",
      description: "Demandez la cl\xE9 de d\xE9chiffrement \xE0 l'exp\xE9diteur par un canal s\xE9par\xE9. La cl\xE9 est utilis\xE9e uniquement dans ce navigateur.",
      required: "Saisissez une cl\xE9 de d\xE9chiffrement.",
      error: "Cette cl\xE9 de d\xE9chiffrement ne permet pas de d\xE9verrouiller le secret. V\xE9rifiez-la et r\xE9essayez.",
      show: "Afficher la cl\xE9 de d\xE9chiffrement",
      hide: "Masquer la cl\xE9 de d\xE9chiffrement",
      change: "Saisir une autre cl\xE9",
      submit: "D\xE9verrouiller le secret"
    },
    credentials: {
      title: "D\xE9verrouiller le secret",
      description: "Saisissez la cl\xE9 de d\xE9chiffrement et le mot de passe fournis par l'exp\xE9diteur. Les deux sont utilis\xE9s uniquement dans ce navigateur.",
      submit: "D\xE9verrouiller le secret"
    },
    legacyKey: {
      warning: "Ce lien a \xE9t\xE9 cr\xE9\xE9 avec un client obsol\xE8te qui place la cl\xE9 de d\xE9chiffrement dans la cha\xEEne de requ\xEAte. Demandez \xE0 l'exp\xE9diteur de mettre \xE0 jour son client pour que les futurs liens gardent la cl\xE9 hors des journaux du serveur."
    },
    content: {
      fileShared: "Un fichier a \xE9t\xE9 partag\xE9 avec vous",
      downloadFile: "T\xE9l\xE9charger le Fichier",
      hideContent: "Masquer le contenu",
      showContent: "Afficher le contenu",
      copyToClipboard: "Copier dans le presse-papiers",
      copiedToClipboard: "Secret copi\xE9 dans le presse-papiers",
      clickToReveal: "Cliquez sur l'ic\xF4ne \u0153il ci-dessus pour r\xE9v\xE9ler le secret",
      passwordProtected: "Ce secret est prot\xE9g\xE9 par un mot de passe. Cliquez pour saisir le mot de passe.",
      ariaLabel: "Contenu secret"
    },
    info: {
      burnedAfterReading: "Ce secret a \xE9t\xE9 supprim\xE9 apr\xE8s votre consultation et ne sera plus disponible apr\xE8s avoir quitt\xE9 la page.",
      expiresIn: "Expire {{time}}"
    },
    errors: {
      notFound: "non trouv\xE9",
      unexpectedStatus: "code de statut inattendu {{code}}"
    },
    actions: {
      viewSecret: "Voir le Secret"
    }
  },
  about: {
    title: "\xC0 propos",
    what: {
      description: "crypt.fyi est un moyen zero-knowledge de partager des informations sensibles \u2014 mots de passe, cl\xE9s API, fichiers \u2014 via un lien. Le chiffrement a lieu dans votre navigateur, nos serveurs ne voient jamais le texte en clair, et les secrets peuvent dispara\xEEtre apr\xE8s lecture.",
      traditionalTitle: "Pourquoi ne pas simplement envoyer un e-mail ou un SMS ?",
      traditionalDescription: "Les e-mails, SMS, Slack et chats conservent des copies : dans les bo\xEEtes de r\xE9ception, l'historique des messages, les journaux des op\xE9rateurs et sur chaque appareil synchronis\xE9. Une fois qu'un mot de passe est coll\xE9 dans ces canaux, vous perdez le contr\xF4le sur qui peut le retrouver plus tard. crypt.fyi est con\xE7u pour une transmission d\xE9lib\xE9r\xE9e, \xE0 usage unique \u2014 partagez le lien, ajoutez \xE9ventuellement un mot de passe ou une liste d'IP, et laissez le secret dispara\xEEtre une fois le besoin pass\xE9."
    },
    letter: {
      title: "Une note de l'auteur",
      p1: "J'ai cr\xE9\xE9 crypt.fyi parce que les outils auxquels je faisais d\xE9j\xE0 appel se montraient insuffisants quand je devais partager quelque chose de sensible \u2014 surtout avec quelqu'un hors de mon gestionnaire de mots de passe \u2014 avec des contr\xF4les adapt\xE9s \xE0 ma fa\xE7on r\xE9elle de travailler.",
      p2: "Les solutions existantes manquaient souvent des d\xE9tails d'UX qui comptent au quotidien : des restrictions riches comme les listes d'IP, les limites de lecture, la destruction apr\xE8s tentatives \xE9chou\xE9es et les webhooks ; une CLI et une extension navigateur ; une configuration sauvegard\xE9e et pr\xE9remplie. Techniquement, certaines \xE9taient aussi en retard sur les standards modernes de chiffrement, ou manquaient d'un vrai read-and-burn atomique pour qu'un secret ne puisse pas \xEAtre lu plusieurs fois \xE0 cause d'une condition de course.",
      p3: "J'ai donc construit quelque chose de nouveau : open source, auto-h\xE9bergeable, et opini\xE2tre sur la cryptographie et les contr\xF4les que je voulais pour moi-m\xEAme.",
      signOff: "\u2014 Dillon"
    },
    openSource: {
      description: "crypt.fyi est open source et auditable. Vous pouvez examiner l'impl\xE9mentation, l'h\xE9berger vous-m\xEAme ou contribuer sur"
    },
    technical: {
      prompt: "Envie d\u2019aller plus loin techniquement ?",
      specLink: "Lire la sp\xE9cification du protocole"
    }
  },
  privacy: {
    title: "Politique de Confidentialit\xE9",
    intro: "Chez crypt.fyi, nous prenons votre confidentialit\xE9 au s\xE9rieux. Cette Politique de Confidentialit\xE9 explique comment nous g\xE9rons vos informations lorsque vous utilisez notre plateforme de partage de secrets \xE0 connaissance nulle et chiffrement de bout en bout.",
    doNotCollect: {
      title: "Informations que Nous ne Collectons pas",
      description: "En raison de notre architecture \xE0 connaissance nulle, nous sommes techniquement incapables d'acc\xE9der \xE0 :",
      items: {
        secrets: "Vos secrets ou fichiers non chiffr\xE9s",
        keys: "Cl\xE9s de chiffrement ou mots de passe",
        urls: "Fragments d'URL contenant des informations de d\xE9chiffrement",
        content: "Le contenu de vos donn\xE9es chiffr\xE9es",
        recipients: "Informations sur les destinataires de vos secrets"
      }
    },
    collect: {
      title: "Informations que Nous Collectons",
      description: "Nous collectons et stockons uniquement les informations minimales requises :",
      items: {
        encrypted: "Donn\xE9es chiffr\xE9es (que nous ne pouvons pas d\xE9chiffrer)",
        hashes: "Hachages de v\xE9rification des cl\xE9s (utilis\xE9s pour v\xE9rifier les droits d'acc\xE8s sans conna\xEEtre les cl\xE9s r\xE9elles)",
        metadata: "M\xE9tadonn\xE9es de base des requ\xEAtes (adresses IP, horodatages) pour la limitation des taux et la pr\xE9vention des abus",
        webhooks: "URLs de webhook si fournies (pour les notifications d'acc\xE8s et de suppression des secrets)"
      },
      note: "Toutes les donn\xE9es stock\xE9es sont automatiquement supprim\xE9es apr\xE8s expiration ou lors de l'acc\xE8s (si la destruction apr\xE8s lecture est activ\xE9e)."
    },
    usage: {
      title: "Comment Nous Utilisons les Informations",
      description: "Nous utilisons les informations collect\xE9es uniquement pour :",
      items: {
        transmission: "Faciliter la transmission s\xE9curis\xE9e de vos secrets chiffr\xE9s",
        rateLimits: "Appliquer des limites de taux pour pr\xE9venir les abus",
        ipControl: "Mettre en \u0153uvre la liste blanche IP/CIDR lorsque configur\xE9e",
        notifications: "Envoyer des notifications webhook lorsqu'activ\xE9es",
        security: "Maintenir la s\xE9curit\xE9 du syst\xE8me et pr\xE9venir les acc\xE8s non autoris\xE9s"
      }
    },
    security: {
      title: "Stockage et S\xE9curit\xE9 des Donn\xE9es",
      description: "Nos mesures de s\xE9curit\xE9 incluent :",
      items: {
        encryption: "Tout le chiffrement/d\xE9chiffrement se fait dans votre navigateur en utilisant ML-KEM post-quantique",
        csp: "Politique de s\xE9curit\xE9 du contenu (CSP) stricte pour pr\xE9venir les attaques XSS",
        tls: "Chiffrement TLS pour toutes les communications API",
        expiration: "Expiration automatique des donn\xE9es avec dur\xE9e de vie configurable (TTL)",
        deletion: "Suppression s\xE9curis\xE9e des donn\xE9es apr\xE8s acc\xE8s ou expiration",
        storage: "Pas de stockage persistant d'informations sensibles"
      }
    },
    thirdParty: {
      title: "Services Tiers",
      description: "Nous ne partageons aucune information avec des tiers sauf dans les cas suivants :",
      items: {
        webhooks: "Lorsque vous activez les webhooks, nous envoyons des notifications aux URLs que vous fournissez concernant l'acc\xE8s et la suppression des secrets",
        infrastructure: "Fournisseurs d'infrastructure qui h\xE9bergent nos services (qui ne voient que des donn\xE9es chiffr\xE9es qu'ils ne peuvent pas d\xE9chiffrer)"
      }
    },
    rights: {
      title: "Vos Droits et Choix",
      description: "Vous avez le contr\xF4le sur vos donn\xE9es :",
      items: {
        expiration: "Choisir des d\xE9lais d'expiration personnalis\xE9s pour vos secrets",
        burn: "Activer la destruction apr\xE8s lecture pour une suppression imm\xE9diate apr\xE8s l'acc\xE8s",
        password: "Ajouter une protection par mot de passe pour une s\xE9curit\xE9 suppl\xE9mentaire",
        ip: "Configurer des restrictions IP pour contr\xF4ler l'acc\xE8s",
        readLimits: "D\xE9finir des limites de lecture"
      }
    },
    changes: {
      title: "Modifications de cette Politique",
      description: "Nous pouvons mettre \xE0 jour cette Politique de Confidentialit\xE9 de temps en temps. Nous informerons les utilisateurs de tout changement important en publiant la nouvelle Politique de Confidentialit\xE9 sur cette page. Nous vous conseillons de consulter r\xE9guli\xE8rement cette Politique de Confidentialit\xE9 pour tout changement."
    },
    contact: {
      title: "Nous Contacter",
      description: "Si vous avez des questions concernant cette Politique de Confidentialit\xE9, vous pouvez nous contacter via notre"
    }
  }
};

// src/i18n/locales/zh.ts
var zh = {
  common: {
    createSecret: "\u521B\u5EFA\u5BC6\u6587",
    starOnGithub: "\u5728 GitHub \u4E0A\u6807\u661F",
    features: "\u529F\u80FD\u7279\u70B9",
    learnMore: "\u4E86\u89E3\u66F4\u591A",
    loading: "\u52A0\u8F7D\u4E2D...",
    create: "\u521B\u5EFA",
    delete: "\u5220\u9664",
    copy: "\u590D\u5236",
    share: "\u5206\u4EAB",
    download: "\u4E0B\u8F7D",
    success: "\u6210\u529F\uFF01",
    error: "\u9519\u8BEF",
    confirm: "\u786E\u8BA4",
    requestNewLanguage: "\u8BF7\u6C42\u65B0\u8BED\u8A00\u6216\u4FEE\u6B63\u7FFB\u8BD1",
    footer: {
      tagline: "\u4EE5\u5B89\u5168\u548C\u9690\u79C1\u4E3A\u8BBE\u8BA1\u7406\u5FF5\uFF0C\u56E0\u4E3A\u65E0\u77E5\u4E5F\u662F\u4E00\u79CD\u5E78\u798F"
    },
    time: {
      minute_one: "{{count}} \u5206\u949F",
      minute_other: "{{count}} \u5206\u949F",
      hour_one: "{{count}} \u5C0F\u65F6",
      hour_other: "{{count}} \u5C0F\u65F6",
      day_one: "{{count}} \u5929",
      day_other: "{{count}} \u5929"
    }
  },
  landing: {
    title: "\u96F6\u77E5\u8BC6\u79D8\u5BC6\u5171\u4EAB",
    subtitle: "\u5728\u6D4F\u89C8\u5668\u4E2D\u52A0\u5BC6\u3002\u5206\u4EAB\u4E00\u6B21\u3002\u6C38\u4E45\u6D88\u5931\u3002\u540E\u91CF\u5B50\u7AEF\u5230\u7AEF\u52A0\u5BC6\u2014\u2014\u6211\u4EEC\u7684\u670D\u52A1\u5668\u6C38\u8FDC\u770B\u4E0D\u5230\u60A8\u7684\u79D8\u5BC6\u3002",
    pillars: {
      title: "\u4E3A\u4FE1\u4EFB\u800C\u6784\u5EFA",
      zeroKnowledge: {
        title: "\u771F\u6B63\u7684\u96F6\u77E5\u8BC6",
        description: "\u52A0\u5BC6\u5BC6\u94A5\u6C38\u8FDC\u4E0D\u4F1A\u79BB\u5F00\u60A8\u7684\u8BBE\u5907\u3002\u89E3\u5BC6\u4EC5\u5728\u63A5\u6536\u8005\u7684\u6D4F\u89C8\u5668\u4E2D\u8FDB\u884C\u2014\u2014\u670D\u52A1\u5668\u6C38\u8FDC\u770B\u4E0D\u5230\u660E\u6587\u3002"
      },
      ephemeral: {
        title: "\u9ED8\u8BA4\u5373\u7528\u5373\u711A",
        description: "\u9605\u540E\u5373\u711A\u3001\u81EA\u5B9A\u4E49 TTL \u548C\u9605\u8BFB\u9650\u5236\uFF0C\u786E\u4FDD\u654F\u611F\u6570\u636E\u4E0D\u4F1A\u5B58\u7559\u8D85\u8FC7\u5FC5\u8981\u65F6\u95F4\u3002"
      },
      defense: {
        title: "\u7EB5\u6DF1\u9632\u5FA1",
        description: "ML-KEM \u540E\u91CF\u5B50\u52A0\u5BC6\u3001\u4E25\u683C CSP\u3001\u901F\u7387\u9650\u5236\u548C\u53EF\u9009 IP \u767D\u540D\u5355\u2014\u2014\u5206\u5C42\u4FDD\u62A4\uFF0C\u800C\u975E\u5355\u4E00\u52FE\u9009\u9879\u3002"
      },
      open: {
        title: "\u5F00\u653E\u4E14\u53EF\u5BA1\u8BA1",
        description: "\u5B8C\u5168\u5F00\u6E90\uFF0C\u4E13\u4E3A\u81EA\u6258\u7BA1\u8BBE\u8BA1\u3002\u68C0\u67E5\u4EE3\u7801\u3001\u8FD0\u884C\u81EA\u5DF1\u7684\u5B9E\u4F8B\u6216\u53C2\u4E0E\u8D21\u732E\u3002"
      }
    },
    alsoIncludes: {
      label: "\u8FD8\u5305\u62EC",
      password: "\u5BC6\u7801\u4FDD\u62A4",
      files: "\u6587\u4EF6\u5171\u4EAB",
      webhooks: "Webhook",
      qrCode: "\u4E8C\u7EF4\u7801",
      ipControl: "IP \u767D\u540D\u5355",
      readLimits: "\u9605\u8BFB\u9650\u5236"
    },
    selfHost: {
      title: "\u8FD0\u884C\u60A8\u81EA\u5DF1\u7684\u5B9E\u4F8B",
      description: "\u5728\u60A8\u7684\u57FA\u7840\u8BBE\u65BD\u4E0A\u90E8\u7F72\u76F8\u540C\u7684\u96F6\u77E5\u8BC6\u6280\u672F\u6808\u2014\u2014\u4E00\u952E\u90E8\u7F72\u5230 Railway\uFF0C\u6216\u4F7F\u7528 Docker Compose \u968F\u5904\u90E8\u7F72\u3002",
      deployOnRailway: "\u90E8\u7F72\u5230 Railway",
      dockerCompose: "Docker Compose"
    },
    ecosystem: {
      cli: "CLI",
      chromeExtension: "Chrome \u6269\u5C55",
      github: "GitHub"
    },
    steps: {
      encrypt: {
        title: "1. \u52A0\u5BC6",
        description: "\u60A8\u7684\u5BC6\u6587\u5728\u5B58\u4E4B\u524D\u4F1A\u5728\u6D4F\u89C8\u5668\u4E2D\u8FDB\u884C\u52A0\u5BC6"
      },
      share: {
        title: "2. \u5206\u4EAB",
        description: "\u5C06\u5B89\u5168\u94FE\u63A5\u53D1\u9001\u7ED9\u9884\u671F\u63A5\u6536\u8005"
      },
      autoDelete: {
        title: "3. \u81EA\u52A8\u5220\u9664",
        description: "\u5BC6\u6587\u5728\u88AB\u67E5\u770B\u540E\u5C06\u6C38\u4E45\u5220\u9664"
      }
    }
  },
  create: {
    form: {
      content: {
        label: "\u5BC6\u6587\u5185\u5BB9",
        placeholder: "\u5728\u6B64\u8F93\u5165\u60A8\u7684\u5BC6\u6587\u5185\u5BB9...",
        fileHint: "\u62D6\u653E\u6216\u70B9\u51FB\u6B64\u5904\u6DFB\u52A0\u6587\u4EF6\uFF08\u6700\u5927 1 MB\uFF09",
        fileSelected: "\u5DF2\u9009\u62E9\u6587\u4EF6\uFF1A{{name}}\uFF08{{size}} KB\uFF09",
        dropFile: "\u62D6\u653E\u6587\u4EF6\u5230\u6B64\u5904",
        invalidFileType: "\u65E0\u6548\u7684\u6587\u4EF6\u7C7B\u578B"
      },
      password: {
        label: "\u5BC6\u7801",
        placeholder: "\u53EF\u9009\uFF08\u4F46\u5EFA\u8BAE\u4F7F\u7528\uFF09"
      },
      ttl: {
        label: "\u6709\u6548\u671F",
        placeholder: "\u9009\u62E9\u8FC7\u671F\u65F6\u95F4"
      },
      burn: {
        label: "\u9605\u540E\u5373\u711A",
        description: "\u786E\u4FDD\u5BC6\u6587\u53EA\u80FD\u88AB\u8BFB\u53D6\u4E00\u6B21\u2014\u2014\u7531\u7B2C\u4E00\u4E2A\u6253\u5F00\u94FE\u63A5\u7684\u4EBA"
      },
      advanced: {
        toggle: "\u9AD8\u7EA7\u914D\u7F6E",
        ip: {
          label: "IP/CIDR \u767D\u540D\u5355",
          placeholder: "192.168.1.1, 10.0.0.0/24, \u7B49",
          description: "\u9650\u5236\u7279\u5B9A IP \u5730\u5740\u6216 CIDR \u5757\u7684\u8BBF\u95EE\uFF08\u9017\u53F7\u5206\u9694\uFF09"
        },
        readCount: {
          label: "\u9605\u8BFB\u6B21\u6570",
          description: "\u5BC6\u6587\u53EF\u88AB\u9605\u8BFB\u7684\u6700\u5927\u6B21\u6570"
        },
        failedAttempts: {
          label: "\u5931\u8D25\u5C1D\u8BD5\u540E\u9500\u6BC1",
          description: "\u5728\u591A\u6B21\u8BBF\u95EE\u5931\u8D25\u5C1D\u8BD5\u540E\u81EA\u52A8\u9500\u6BC1\u5BC6\u6587"
        },
        webhook: {
          label: "Webhook",
          placeholder: "https://example.com/webhook",
          description: "\u5F53\u5BC6\u6587\u88AB\u8BFB\u53D6\u3001\u9500\u6BC1\u6216\u8BFB\u53D6\u5931\u8D25\u65F6\u8C03\u7528\u7684 Webhook URL\uFF08\u5982\u679C\u8BBE\u7F6E\uFF0C\u53EF\u914D\u7F6E\uFF09",
          read: "\u8BFB\u53D6",
          burn: "\u9500\u6BC1",
          failureToReadPK: "\u5931\u8D25\uFF08\u5BC6\u7801\u6216\u5BC6\u94A5\uFF09",
          failureToReadIP: "\u5931\u8D25\uFF08IP \u6216 CIDR\uFF09",
          nameLabel: "\u540D\u79F0",
          namePlaceholder: "\u8F93\u5165\u6B64 webhook \u7684\u540D\u79F0",
          nameDescription: "\u7528\u4E8E\u5728\u901A\u77E5\u4E2D\u8BC6\u522B\u6B64 webhook \u7684\u540D\u79F0"
        }
      }
    },
    success: {
      title: "\u5BC6\u6587\u5DF2\u521B\u5EFA\uFF01",
      description: {
        main: "\u60A8\u7684\u5BC6\u6587\u5DF2\u521B\u5EFA\u3002\u8BF7\u4ECE\u4E0B\u65B9\u9009\u62E9\u5206\u4EAB\u65B9\u5F0F\u3002",
        password: "\u8BF7\u901A\u8FC7\u5355\u72EC\u7684\u6E20\u9053\u53D1\u9001\u5BC6\u7801\u3002",
        separateKey: "\u82E5\u8981\u5206\u5F00\u53D1\u9001\uFF0C\u8BF7\u901A\u8FC7\u4E0D\u540C\u6E20\u9053\u53D1\u9001\u65E0\u5BC6\u94A5 URL \u548C\u89E3\u5BC6\u5BC6\u94A5\u3002"
      },
      combinedUrl: "\u5B8C\u6574 URL\uFF08\u542B\u5BC6\u94A5\uFF09",
      keylessUrl: "\u65E0\u5BC6\u94A5 URL",
      decryptionKey: "\u89E3\u5BC6\u5BC6\u94A5",
      urlCopied: "URL \u5DF2\u590D\u5236\u5230\u526A\u8D34\u677F",
      keyCopied: "\u89E3\u5BC6\u5BC6\u94A5\u5DF2\u590D\u5236\u5230\u526A\u8D34\u677F",
      secretDeleted: "\u5BC6\u6587\u5DF2\u5220\u9664",
      qrDownloaded: "\u4E8C\u7EF4\u7801\u5DF2\u4E0B\u8F7D",
      qrDownloadFailed: "\u4E0B\u8F7D\u4E8C\u7EF4\u7801\u5931\u8D25\uFF1A{{error}}",
      qrCode: {
        title: "\u5BC6\u6587 URL \u4E8C\u7EF4\u7801",
        description: "\u4E0B\u8F7D\u5E76\u5206\u4EAB\u5BC6\u6587 URL \u4E8C\u7EF4\u7801"
      },
      actions: {
        showUrl: "\u663E\u793A\u5BC6\u6587 URL",
        hideUrl: "\u9690\u85CF\u5BC6\u6587 URL",
        showKey: "\u663E\u793A\u89E3\u5BC6\u5BC6\u94A5",
        hideKey: "\u9690\u85CF\u89E3\u5BC6\u5BC6\u94A5",
        shareUrl: "\u5206\u4EAB\u5BC6\u6587 URL",
        shareKey: "\u5206\u4EAB\u89E3\u5BC6\u5BC6\u94A5",
        copyUrl: "\u590D\u5236\u5BC6\u6587 URL",
        copyKey: "\u590D\u5236\u89E3\u5BC6\u5BC6\u94A5",
        showQr: "\u663E\u793A\u5B8C\u6574 URL \u4E8C\u7EF4\u7801"
      },
      createAnother: "\u521B\u5EFA\u53E6\u4E00\u4E2A",
      deleteSecret: "\u5220\u9664\u5BC6\u6587",
      info: {
        expires: "\u8FC7\u671F\u65F6\u95F4\uFF1A{{time}}",
        burn: "\u5BC6\u6587\u5C06\u5728\u67E5\u770B\u540E\u5220\u9664",
        passwordProtected: "\u5BC6\u7801\u4FDD\u62A4",
        ipRestrictions: "IP\u9650\u5236\uFF1A{{ips}}",
        readCount: "\u9605\u8BFB\u6B21\u6570\uFF1A{{count}}",
        webhook: "Webhook\u5DF2\u914D\u7F6E\uFF1A{{events}} ({{url}})",
        failureCount: "\u5BC6\u6587\u5C06\u5728{{count}}\u6B21\u5931\u8D25\u5C1D\u8BD5\u540E\u5220\u9664"
      }
    },
    errors: {
      contentRequired: "\u5185\u5BB9\u4E3A\u5FC5\u586B\u9879",
      readCountWithBurn: "\u9605\u540E\u5373\u711A\u6A21\u5F0F\u4E0B\u4E0D\u80FD\u8BBE\u7F6E\u9605\u8BFB\u6B21\u6570",
      tooManyIps: "IP \u9650\u5236\u8FC7\u591A\uFF08\u6700\u5927 {{max}}\uFF09",
      invalidIp: "\u65E0\u6548\u7684 IP \u5730\u5740\u6216 CIDR \u5757\uFF1A{{ip}}",
      uploadFailed: "\u5904\u7406\u4E0A\u4F20\u5185\u5BB9\u5931\u8D25\uFF1A{{error}}",
      deleteFailed: "\u5220\u9664\u5BC6\u6587\u5931\u8D25\uFF1A{{error}}",
      secretNotFound: "\u672A\u627E\u5230\u5BC6\u6587",
      unexpectedStatus: "\u610F\u5916\u7684\u72B6\u6001\u7801 {{code}}",
      webhookConfigInvalid: "Webhook\u914D\u7F6E\u65E0\u6548 - \u81F3\u5C11\u9700\u8981\u4E00\u4E2AWebhook\u4E8B\u4EF6\u7C7B\u578B",
      fileSizeExceeded: "\u6587\u4EF6\u8FC7\u5927\u3002\u6700\u5927\u5927\u5C0F\u4E3A {{max}}\u3002",
      fileReadError: "\u8BFB\u53D6\u6587\u4EF6\u5931\u8D25",
      fileReadAborted: "\u6587\u4EF6\u8BFB\u53D6\u5DF2\u4E2D\u6B62"
    }
  },
  view: {
    notFound: {
      title: "\u672A\u627E\u5230\u5BC6\u6587",
      description: "\u6B64\u5BC6\u6587\u53EF\u80FD\u5DF2\u8FC7\u671F\u6216\u88AB\u5220\u9664\u3002",
      createNew: "\u521B\u5EFA\u65B0\u5BC6\u6587"
    },
    invalidLink: {
      title: "\u6B64\u94FE\u63A5\u65E0\u6548",
      description: "\u94FE\u63A5\u4E0D\u5B8C\u6574\u6216\u5728\u4F20\u8F93\u8FC7\u7A0B\u4E2D\u88AB\u66F4\u6539\uFF0C\u56E0\u6B64\u65E0\u6CD5\u89E3\u5BC6\u5BC6\u6587\u3002\u8BF7\u8BA9\u53D1\u9001\u8005\u91CD\u65B0\u5206\u4EAB\u5B8C\u6574\u94FE\u63A5\u3002",
      createNew: "\u521B\u5EFA\u65B0\u5BC6\u6587"
    },
    connectionError: {
      title: "\u65E0\u6CD5\u8FDE\u63A5\u670D\u52A1\u5668",
      description: "\u5BC6\u6587\u53EF\u80FD\u4ECD\u7136\u5B58\u5728\u3002\u8BF7\u68C0\u67E5\u60A8\u7684\u7F51\u7EDC\u8FDE\u63A5\u540E\u91CD\u8BD5\u3002",
      tryAgain: "\u91CD\u8BD5"
    },
    rateLimit: {
      title: "\u8BF7\u6C42\u8FC7\u591A",
      description: "\u60A8\u7684\u8BF7\u6C42\u8FC7\u4E8E\u9891\u7E41\u3002\u8BF7\u7A0D\u7B49\u7247\u523B\u540E\u91CD\u8BD5\u3002",
      tryAgain: "\u91CD\u8BD5"
    },
    password: {
      title: "\u8F93\u5165\u5BC6\u7801",
      label: "\u5BC6\u7801",
      placeholder: "\u8F93\u5165\u5BC6\u7801",
      description: "\u6B64\u5BC6\u6587\u53D7\u5BC6\u7801\u4FDD\u62A4 - \u8BF7\u5411\u53D1\u9001\u8005\u7D22\u8981\u5BC6\u7801",
      error: "\u89E3\u5BC6\u5BC6\u94A5\u6216\u5BC6\u7801\u9519\u8BEF",
      required: "\u8BF7\u8F93\u5165\u5BC6\u7801\u3002",
      show: "\u663E\u793A\u5BC6\u7801",
      hide: "\u9690\u85CF\u5BC6\u7801"
    },
    key: {
      title: "\u8F93\u5165\u89E3\u5BC6\u5BC6\u94A5",
      label: "\u89E3\u5BC6\u5BC6\u94A5",
      placeholder: "\u8F93\u5165\u53D1\u9001\u8005\u63D0\u4F9B\u7684\u5BC6\u94A5",
      description: "\u8BF7\u901A\u8FC7\u5355\u72EC\u7684\u6E20\u9053\u5411\u53D1\u9001\u8005\u7D22\u53D6\u89E3\u5BC6\u5BC6\u94A5\u3002\u8BE5\u5BC6\u94A5\u4EC5\u5728\u6B64\u6D4F\u89C8\u5668\u4E2D\u4F7F\u7528\u3002",
      required: "\u8BF7\u8F93\u5165\u89E3\u5BC6\u5BC6\u94A5\u3002",
      error: "\u6B64\u89E3\u5BC6\u5BC6\u94A5\u65E0\u6CD5\u89E3\u9501\u5BC6\u6587\u3002\u8BF7\u68C0\u67E5\u5BC6\u94A5\u540E\u91CD\u8BD5\u3002",
      show: "\u663E\u793A\u89E3\u5BC6\u5BC6\u94A5",
      hide: "\u9690\u85CF\u89E3\u5BC6\u5BC6\u94A5",
      change: "\u8F93\u5165\u5176\u4ED6\u5BC6\u94A5",
      submit: "\u89E3\u9501\u5BC6\u6587"
    },
    credentials: {
      title: "\u89E3\u9501\u5BC6\u6587",
      description: "\u8BF7\u8F93\u5165\u53D1\u9001\u8005\u63D0\u4F9B\u7684\u89E3\u5BC6\u5BC6\u94A5\u548C\u5BC6\u7801\u3002\u4E24\u8005\u4EC5\u5728\u6B64\u6D4F\u89C8\u5668\u4E2D\u4F7F\u7528\u3002",
      submit: "\u89E3\u9501\u5BC6\u6587"
    },
    legacyKey: {
      warning: "\u6B64\u94FE\u63A5\u7531\u8FC7\u65F6\u7684\u5BA2\u6237\u7AEF\u521B\u5EFA\uFF0C\u8BE5\u5BA2\u6237\u7AEF\u5C06\u89E3\u5BC6\u5BC6\u94A5\u653E\u5728\u67E5\u8BE2\u5B57\u7B26\u4E32\u4E2D\u3002\u8BF7\u8BA9\u53D1\u9001\u8005\u5347\u7EA7\u5BA2\u6237\u7AEF\uFF0C\u4EE5\u4FBF\u540E\u7EED\u94FE\u63A5\u5C06\u5BC6\u94A5\u6392\u9664\u5728\u670D\u52A1\u5668\u65E5\u5FD7\u4E4B\u5916\u3002"
    },
    content: {
      fileShared: "\u6709\u4EBA\u4E0E\u60A8\u5206\u4EAB\u4E86\u4E00\u4E2A\u6587\u4EF6",
      downloadFile: "\u4E0B\u8F7D\u6587\u4EF6",
      hideContent: "\u9690\u85CF\u5185\u5BB9",
      showContent: "\u663E\u793A\u5185\u5BB9",
      copyToClipboard: "\u590D\u5236\u5230\u526A\u8D34\u677F",
      copiedToClipboard: "\u5BC6\u6587\u5DF2\u590D\u5236\u5230\u526A\u8D34\u677F",
      clickToReveal: "\u70B9\u51FB\u4E0A\u65B9\u7684\u773C\u775B\u56FE\u6807\u663E\u793A\u5BC6\u6587",
      passwordProtected: "\u6B64\u5BC6\u6587\u53D7\u5BC6\u7801\u4FDD\u62A4\u3002\u70B9\u51FB\u8F93\u5165\u5BC6\u7801\u3002",
      ariaLabel: "\u5BC6\u6587\u5185\u5BB9"
    },
    info: {
      burnedAfterReading: "\u6B64\u5BC6\u6587\u5728\u60A8\u67E5\u770B\u540E\u5DF2\u88AB\u5220\u9664\uFF0C\u79BB\u5F00\u9875\u9762\u540E\u5C06\u65E0\u6CD5\u518D\u6B21\u8BBF\u95EE\u3002",
      expiresIn: "{{time}}\u540E\u8FC7\u671F"
    },
    errors: {
      notFound: "\u672A\u627E\u5230",
      unexpectedStatus: "\u610F\u5916\u7684\u72B6\u6001\u7801 {{code}}"
    },
    actions: {
      viewSecret: "\u67E5\u770B\u5BC6\u6587"
    }
  },
  about: {
    title: "\u5173\u4E8E",
    what: {
      description: "crypt.fyi \u662F\u4E00\u79CD\u96F6\u77E5\u8BC6\u65B9\u5F0F\uFF0C\u901A\u8FC7\u94FE\u63A5\u5206\u4EAB\u654F\u611F\u4FE1\u606F\u2014\u2014\u5BC6\u7801\u3001API \u5BC6\u94A5\u3001\u6587\u4EF6\u3002\u52A0\u5BC6\u5728\u60A8\u7684\u6D4F\u89C8\u5668\u4E2D\u5B8C\u6210\uFF0C\u6211\u4EEC\u7684\u670D\u52A1\u5668\u6C38\u8FDC\u770B\u4E0D\u5230\u660E\u6587\uFF0C\u79D8\u5BC6\u53EF\u5728\u88AB\u8BFB\u53D6\u540E\u6D88\u5931\u3002",
      traditionalTitle: "\u4E3A\u4EC0\u4E48\u4E0D\u76F4\u63A5\u53D1\u90AE\u4EF6\u6216\u77ED\u4FE1\uFF1F",
      traditionalDescription: "\u90AE\u4EF6\u3001\u77ED\u4FE1\u3001Slack \u548C\u804A\u5929\u90FD\u4F1A\u7559\u4E0B\u526F\u672C\uFF1A\u6536\u4EF6\u7BB1\u3001\u6D88\u606F\u8BB0\u5F55\u3001\u8FD0\u8425\u5546\u65E5\u5FD7\uFF0C\u4EE5\u53CA\u6BCF\u53F0\u540C\u6B65\u8FC7\u7684\u8BBE\u5907\u3002\u4E00\u65E6\u628A\u5BC6\u7801\u7C98\u8D34\u5230\u8FD9\u4E9B\u6E20\u9053\uFF0C\u4F60\u5C31\u5931\u53BB\u4E86\u5BF9\u8C01\u80FD\u4E8B\u540E\u627E\u5230\u5B83\u7684\u63A7\u5236\u3002crypt.fyi \u7528\u4E8E\u4E00\u6B21\u523B\u610F\u7684\u4EA4\u63A5\u2014\u2014\u5206\u4EAB\u94FE\u63A5\uFF0C\u53EF\u9009\u6DFB\u52A0\u5BC6\u7801\u6216 IP \u767D\u540D\u5355\uFF0C\u5E76\u5728\u7528\u5B8C\u540E\u8BA9\u79D8\u5BC6\u6D88\u5931\u3002"
    },
    letter: {
      title: "\u4F5C\u8005\u7684\u4E00\u5C01\u77ED\u7B3A",
      p1: "\u6211\u521B\u5EFA crypt.fyi\uFF0C\u662F\u56E0\u4E3A\u6211\u5DF2\u7ECF\u5728\u7528\u7684\u5DE5\u5177\uFF0C\u5728\u6211\u9700\u8981\u5206\u4EAB\u654F\u611F\u5185\u5BB9\u65F6\u2014\u2014\u5C24\u5176\u662F\u5BF9\u5BC6\u7801\u7BA1\u7406\u5668\u4E4B\u5916\u7684\u4EBA\u2014\u2014\u5F80\u5F80\u7F3A\u5C11\u771F\u6B63\u7B26\u5408\u6211\u5DE5\u4F5C\u65B9\u5F0F\u7684\u63A7\u5236\u80FD\u529B\u3002",
      p2: "\u73B0\u6709\u65B9\u6848\u5E38\u5E38\u7F3A\u5C11\u65E5\u5E38\u771F\u6B63\u91CD\u8981\u7684\u4F53\u9A8C\u7EC6\u8282\uFF1A\u4E30\u5BCC\u7684\u9650\u5236\u80FD\u529B\uFF08\u5982 IP \u767D\u540D\u5355\u3001\u9605\u8BFB\u6B21\u6570\u3001\u5931\u8D25\u5C1D\u8BD5\u540E\u9500\u6BC1\u3001Webhook\uFF09\uFF1BCLI \u4E0E\u6D4F\u89C8\u5668\u6269\u5C55\uFF1B\u5DF2\u4FDD\u5B58/\u9884\u586B\u914D\u7F6E\u3002\u6280\u672F\u4E0A\uFF0C\u6709\u4E9B\u4E5F\u843D\u540E\u4E8E\u73B0\u4EE3\u52A0\u5BC6\u6807\u51C6\uFF0C\u6216\u7F3A\u5C11\u771F\u6B63\u7684\u539F\u5B50\u8BFB\u540E\u5373\u711A\uFF0C\u4ECE\u800C\u65E0\u6CD5\u907F\u514D\u7ADE\u6001\u6761\u4EF6\u4E0B\u88AB\u591A\u6B21\u8BFB\u53D6\u3002",
      p3: "\u4E8E\u662F\u6211\u505A\u4E86\u65B0\u7684\u4E1C\u897F\uFF1A\u5F00\u6E90\u3001\u53EF\u81EA\u6258\u7BA1\uFF0C\u5E76\u5728\u6211\u81EA\u5DF1\u60F3\u8981\u7684\u5BC6\u7801\u5B66\u4E0E\u63A7\u5236\u80FD\u529B\u4E0A\u65D7\u5E1C\u9C9C\u660E\u3002",
      signOff: "\u2014 Dillon"
    },
    openSource: {
      description: "crypt.fyi \u5F00\u6E90\u4E14\u53EF\u5BA1\u8BA1\u3002\u4F60\u53EF\u4EE5\u5BA1\u67E5\u5B9E\u73B0\u3001\u81EA\u884C\u6258\u7BA1\uFF0C\u6216\u5728\u6B64\u8D21\u732E\uFF1A"
    },
    technical: {
      prompt: "\u60F3\u4E86\u89E3\u66F4\u591A\u6280\u672F\u7EC6\u8282\uFF1F",
      specLink: "\u9605\u8BFB\u534F\u8BAE\u89C4\u8303"
    }
  },
  privacy: {
    title: "\u9690\u79C1\u653F\u7B56",
    intro: "\u5728 crypt.fyi\uFF0C\u6211\u4EEC\u4E25\u8083\u5BF9\u5F85\u60A8\u7684\u9690\u79C1\u3002\u672C\u9690\u79C1\u653F\u7B56\u8BF4\u660E\u4E86\u5F53\u60A8\u4F7F\u7528\u6211\u4EEC\u7684\u96F6\u77E5\u8BC6\u3001\u7AEF\u5230\u7AEF\u52A0\u5BC6\u7684\u79D8\u5BC6\u5171\u4EAB\u5E73\u53F0\u65F6\uFF0C\u6211\u4EEC\u5982\u4F55\u5904\u7406\u60A8\u7684\u4FE1\u606F\u3002",
    doNotCollect: {
      title: "\u6211\u4EEC\u4E0D\u6536\u96C6\u7684\u4FE1\u606F",
      description: "\u7531\u4E8E\u6211\u4EEC\u7684\u96F6\u77E5\u8BC6\u67B6\u6784\uFF0C\u6211\u4EEC\u5728\u6280\u672F\u4E0A\u65E0\u6CD5\u8BBF\u95EE\uFF1A",
      items: {
        secrets: "\u60A8\u7684\u672A\u52A0\u5BC6\u79D8\u5BC6\u6216\u6587\u4EF6",
        keys: "\u52A0\u5BC6\u5BC6\u94A5\u6216\u5BC6\u7801",
        urls: "\u5305\u542B\u89E3\u5BC6\u4FE1\u606F\u7684 URL \u7247\u6BB5",
        content: "\u60A8\u7684\u52A0\u5BC6\u6570\u636E\u7684\u5185\u5BB9",
        recipients: "\u60A8\u7684\u79D8\u5BC6\u63A5\u6536\u8005\u7684\u4FE1\u606F"
      }
    },
    collect: {
      title: "\u6211\u4EEC\u6536\u96C6\u7684\u4FE1\u606F",
      description: "\u6211\u4EEC\u4EC5\u6536\u96C6\u548C\u5B58\u50A8\u6700\u5C11\u5FC5\u9700\u7684\u4FE1\u606F\uFF1A",
      items: {
        encrypted: "\u52A0\u5BC6\u6570\u636E\uFF08\u6211\u4EEC\u65E0\u6CD5\u89E3\u5BC6\uFF09",
        hashes: "\u5BC6\u94A5\u9A8C\u8BC1\u54C8\u5E0C\uFF08\u7528\u4E8E\u9A8C\u8BC1\u8BBF\u95EE\u6743\u9650\u800C\u65E0\u9700\u77E5\u9053\u5B9E\u9645\u5BC6\u94A5\uFF09",
        metadata: "\u57FA\u672C\u8BF7\u6C42\u5143\u6570\u636E\uFF08IP \u5730\u5740\u3001\u65F6\u95F4\u6233\uFF09\u7528\u4E8E\u901F\u7387\u9650\u5236\u548C\u6EE5\u7528\u9884\u9632",
        webhooks: "\u5982\u679C\u63D0\u4F9B\u4E86 webhook URL\uFF08\u7528\u4E8E\u79D8\u5BC6\u8BBF\u95EE\u548C\u5220\u9664\u901A\u77E5\uFF09"
      },
      note: "\u6240\u6709\u5B58\u50A8\u7684\u6570\u636E\u5728\u8FC7\u671F\u540E\u6216\u8BBF\u95EE\u65F6\u81EA\u52A8\u5220\u9664\uFF08\u5982\u679C\u542F\u7528\u4E86\u9605\u540E\u5373\u711A\uFF09\u3002"
    },
    usage: {
      title: "\u6211\u4EEC\u5982\u4F55\u4F7F\u7528\u4FE1\u606F",
      description: "\u6211\u4EEC\u4EC5\u5C06\u6536\u96C6\u7684\u4FE1\u606F\u7528\u4E8E\uFF1A",
      items: {
        transmission: "\u4FC3\u8FDB\u60A8\u7684\u52A0\u5BC6\u79D8\u5BC6\u7684\u5B89\u5168\u4F20\u8F93",
        rateLimits: "\u5B9E\u65BD\u901F\u7387\u9650\u5236\u4EE5\u9632\u6B62\u6EE5\u7528",
        ipControl: "\u5728\u914D\u7F6E\u65F6\u5B9E\u65BD IP/CIDR \u767D\u540D\u5355",
        notifications: "\u5728\u542F\u7528\u65F6\u53D1\u9001 webhook \u901A\u77E5",
        security: "\u7EF4\u62A4\u7CFB\u7EDF\u5B89\u5168\u5E76\u9632\u6B62\u672A\u6388\u6743\u8BBF\u95EE"
      }
    },
    security: {
      title: "\u6570\u636E\u5B58\u50A8\u548C\u5B89\u5168",
      description: "\u6211\u4EEC\u7684\u5B89\u5168\u63AA\u65BD\u5305\u62EC\uFF1A",
      items: {
        encryption: "\u6240\u6709\u52A0\u5BC6/\u89E3\u5BC6\u90FD\u5728\u60A8\u7684\u6D4F\u89C8\u5668\u4E2D\u4F7F\u7528 ML-KEM \u540E\u91CF\u5B50\u52A0\u5BC6\u8FDB\u884C",
        csp: "\u4E25\u683C\u7684\u5185\u5BB9\u5B89\u5168\u7B56\u7565 (CSP) \u4EE5\u9632\u6B62 XSS \u653B\u51FB",
        tls: "\u6240\u6709 API \u901A\u4FE1\u90FD\u4F7F\u7528 TLS \u52A0\u5BC6",
        expiration: "\u5177\u6709\u53EF\u914D\u7F6E\u751F\u5B58\u65F6\u95F4 (TTL) \u7684\u81EA\u52A8\u6570\u636E\u8FC7\u671F",
        deletion: "\u8BBF\u95EE\u6216\u8FC7\u671F\u540E\u5B89\u5168\u5220\u9664\u6570\u636E",
        storage: "\u4E0D\u6301\u4E45\u5B58\u50A8\u654F\u611F\u4FE1\u606F"
      }
    },
    thirdParty: {
      title: "\u7B2C\u4E09\u65B9\u670D\u52A1",
      description: "\u9664\u4EE5\u4E0B\u60C5\u51B5\u5916\uFF0C\u6211\u4EEC\u4E0D\u4E0E\u7B2C\u4E09\u65B9\u5171\u4EAB\u4FE1\u606F\uFF1A",
      items: {
        webhooks: "\u5F53\u60A8\u542F\u7528 webhooks \u65F6\uFF0C\u6211\u4EEC\u5411\u60A8\u63D0\u4F9B\u7684 URL \u53D1\u9001\u6709\u5173\u79D8\u5BC6\u8BBF\u95EE\u548C\u5220\u9664\u7684\u901A\u77E5",
        infrastructure: "\u6258\u7BA1\u6211\u4EEC\u670D\u52A1\u7684\u57FA\u7840\u8BBE\u65BD\u63D0\u4F9B\u5546\uFF08\u4ED6\u4EEC\u53EA\u80FD\u770B\u5230\u65E0\u6CD5\u89E3\u5BC6\u7684\u52A0\u5BC6\u6570\u636E\uFF09"
      }
    },
    rights: {
      title: "\u60A8\u7684\u6743\u5229\u548C\u9009\u62E9",
      description: "\u60A8\u53EF\u4EE5\u63A7\u5236\u60A8\u7684\u6570\u636E\uFF1A",
      items: {
        expiration: "\u4E3A\u60A8\u7684\u79D8\u5BC6\u9009\u62E9\u81EA\u5B9A\u4E49\u8FC7\u671F\u65F6\u95F4",
        burn: "\u542F\u7528\u9605\u540E\u5373\u711A\u4EE5\u5728\u8BBF\u95EE\u540E\u7ACB\u5373\u5220\u9664",
        password: "\u6DFB\u52A0\u5BC6\u7801\u4FDD\u62A4\u4EE5\u589E\u52A0\u5B89\u5168\u6027",
        ip: "\u914D\u7F6E IP \u9650\u5236\u4EE5\u63A7\u5236\u8BBF\u95EE",
        readLimits: "\u8BBE\u7F6E\u9605\u8BFB\u9650\u5236"
      }
    },
    changes: {
      title: "\u672C\u653F\u7B56\u7684\u53D8\u66F4",
      description: "\u6211\u4EEC\u53EF\u80FD\u4F1A\u4E0D\u65F6\u66F4\u65B0\u672C\u9690\u79C1\u653F\u7B56\u3002\u6211\u4EEC\u4F1A\u901A\u8FC7\u5728\u6B64\u9875\u9762\u4E0A\u53D1\u5E03\u65B0\u7684\u9690\u79C1\u653F\u7B56\u6765\u901A\u77E5\u7528\u6237\u91CD\u5927\u53D8\u66F4\u3002\u6211\u4EEC\u5EFA\u8BAE\u60A8\u5B9A\u671F\u67E5\u770B\u672C\u9690\u79C1\u653F\u7B56\u4EE5\u4E86\u89E3\u4EFB\u4F55\u53D8\u66F4\u3002"
    },
    contact: {
      title: "\u8054\u7CFB\u6211\u4EEC",
      description: "\u5982\u679C\u60A8\u5BF9\u672C\u9690\u79C1\u653F\u7B56\u6709\u4EFB\u4F55\u95EE\u9898\uFF0C\u60A8\u53EF\u4EE5\u901A\u8FC7\u6211\u4EEC\u7684"
    }
  }
};

// src/i18n/index.ts
var resources = {
  en: {
    translation: en
  },
  es: {
    translation: es
  },
  de: {
    translation: de
  },
  fr: {
    translation: fr
  },
  zh: {
    translation: zh
  }
};
var supportedLanguages = {
  en: "English",
  es: "Espa\xF1ol",
  de: "Deutsch",
  fr: "Fran\xE7ais",
  zh: "\u4E2D\u6587"
};
var supportedLanguagesOptions = Object.entries(supportedLanguages).map(([key, value]) => ({
  value: key,
  label: value
}));

// src/buffer.ts
import { Buffer as Buffer2 } from "buffer";

// src/random.ts
import { randomBytes } from "@noble/hashes/utils";
async function generateRandomBytes(length) {
  return randomBytes(length);
}
async function generateRandomHexString(length) {
  const bytes = await generateRandomBytes(length);
  return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
var URL_SAFE_DICTIONARY = `ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_~`;
async function generateRandomString(length, dictionary = URL_SAFE_DICTIONARY) {
  const dictionaryLength = dictionary.length;
  const maxValid = Math.floor(256 / dictionaryLength) * dictionaryLength - 1;
  let result = "";
  while (result.length < length) {
    const bytes = await generateRandomBytes(length - result.length);
    for (const byte of bytes) {
      if (byte > maxValid) continue;
      result += dictionary.charAt(byte % dictionaryLength);
      if (result.length === length) break;
    }
  }
  return result;
}

// src/verification.ts
import { argon2idAsync } from "@noble/hashes/argon2";
import { sha256 as nobleSha2562 } from "@noble/hashes/sha2";
import { utf8ToBytes } from "@noble/hashes/utils";

// src/hash.ts
import { sha256 as nobleSha256 } from "@noble/hashes/sha2";
import { sha512 as nobleSha512 } from "@noble/hashes/sha2";
var sha256 = (input) => Buffer2.from(nobleSha256(input)).toString("hex");
var sha512 = (input) => Buffer2.from(nobleSha512(input)).toString("hex");

// src/kdf.ts
var PRODUCTION_ARGON2_CONTENT = {
  t: 2,
  m: 19456,
  // 19 MiB
  p: 1,
  dkLen: 64
  // ML-KEM-768 seed
};
var PRODUCTION_ARGON2_VERIFICATION = {
  t: 2,
  m: 19456,
  // 19 MiB
  p: 1,
  dkLen: 32
};
var PRODUCTION_PBKDF2_ITERATIONS = 2 ** 19;
var argon2Content = { ...PRODUCTION_ARGON2_CONTENT };
var argon2Verification = { ...PRODUCTION_ARGON2_VERIFICATION };
var pbkdf2Iterations = PRODUCTION_PBKDF2_ITERATIONS;
var getArgon2ContentParams = () => argon2Content;
var getArgon2VerificationParams = () => argon2Verification;
var getPbkdf2Iterations = () => pbkdf2Iterations;

// src/verification.ts
var KEY_VERSION_2_PREFIX = "2.";
var parseKey = (key) => {
  if (key.startsWith(KEY_VERSION_2_PREFIX)) {
    return { scheme: "v2", rawKey: key.slice(KEY_VERSION_2_PREFIX.length) };
  }
  return { scheme: "legacy", rawKey: key };
};
var deriveVerificationHash = async (scheme, rawKey, password) => {
  const secret = rawKey + (password ?? "");
  if (scheme === "legacy") {
    return sha512(secret);
  }
  const salt = nobleSha2562(utf8ToBytes(rawKey));
  const derived = await argon2idAsync(utf8ToBytes(secret), salt, getArgon2VerificationParams());
  return Buffer2.from(derived).toString("hex");
};

// src/encryption/gcm.ts
var gcm_exports = {};
__export(gcm_exports, {
  decrypt: () => decrypt,
  encrypt: () => encrypt
});
import { gcm } from "@noble/ciphers/aes";
import { pbkdf2Async } from "@noble/hashes/pbkdf2";
import { sha256 as nobleSha2563 } from "@noble/hashes/sha2";
import { randomBytes as randomBytes2 } from "@noble/hashes/utils";
import { concatBytes, utf8ToBytes as utf8ToBytes2 } from "@noble/hashes/utils";

// src/encryption/encryption.ts
var DecryptError = class extends Error {
  constructor(error) {
    super("decrypt error");
    this.name = "DecryptError";
    this.error = error;
  }
};
var EncryptError = class extends Error {
  constructor(error) {
    super("encrypt error");
    this.name = "EncryptError";
    this.error = error;
  }
};

// src/encryption/gcm.ts
var SALT_LENGTH = 16;
var IV_LENGTH = 12;
var KEY_LENGTH = 32;
var encrypt = async (content, password) => {
  try {
    const salt = randomBytes2(SALT_LENGTH);
    const key = await pbkdf2Async(nobleSha2563, utf8ToBytes2(password), salt, {
      c: getPbkdf2Iterations(),
      dkLen: KEY_LENGTH
    });
    const iv = randomBytes2(IV_LENGTH);
    const cipher = gcm(key, iv);
    const encrypted = cipher.encrypt(utf8ToBytes2(content));
    const result = concatBytes(salt, iv, encrypted);
    return Buffer2.from(result).toString("base64");
  } catch (error) {
    throw new EncryptError(error);
  }
};
var decrypt = async (encryptedContent, password) => {
  try {
    const data = Buffer2.from(encryptedContent, "base64");
    const salt = data.subarray(0, SALT_LENGTH);
    const iv = data.subarray(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
    const ciphertext = data.subarray(SALT_LENGTH + IV_LENGTH);
    const key = await pbkdf2Async(nobleSha2563, utf8ToBytes2(password), salt, {
      c: getPbkdf2Iterations(),
      dkLen: KEY_LENGTH
    });
    const cipher = gcm(key, iv);
    const decrypted = cipher.decrypt(ciphertext);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    throw new DecryptError(error);
  }
};

// src/encryption/mlkem.ts
var mlkem_exports = {};
__export(mlkem_exports, {
  decrypt: () => decrypt2,
  encrypt: () => encrypt2
});
import { ml_kem768 } from "@noble/post-quantum/ml-kem";
import { randomBytes as randomBytes3 } from "@noble/post-quantum/utils";
import { concatBytes as concatBytes2, utf8ToBytes as utf8ToBytes3 } from "@noble/hashes/utils";
import { gcm as gcm2 } from "@noble/ciphers/aes";
import { sha512 as sha5122 } from "@noble/hashes/sha2";
var IV_LENGTH2 = 12;
var CIPHERTEXT_LENGTH = 1088;
var encrypt2 = async (content, password) => {
  try {
    const passwordBytes = utf8ToBytes3(password);
    const seed = sha5122(passwordBytes);
    const derivedKeyPair = ml_kem768.keygen(seed);
    const { cipherText, sharedSecret } = ml_kem768.encapsulate(derivedKeyPair.publicKey);
    const iv = randomBytes3(IV_LENGTH2);
    const cipher = gcm2(sharedSecret, iv);
    const encryptedContent = cipher.encrypt(utf8ToBytes3(content));
    const result = concatBytes2(cipherText, iv, encryptedContent);
    return Buffer2.from(result).toString("base64");
  } catch (error) {
    throw new EncryptError(error);
  }
};
var decrypt2 = async (encryptedContent, password) => {
  try {
    const data = Buffer2.from(encryptedContent, "base64");
    const cipherText = data.subarray(0, CIPHERTEXT_LENGTH);
    const iv = data.subarray(CIPHERTEXT_LENGTH, CIPHERTEXT_LENGTH + IV_LENGTH2);
    const encrypted = data.subarray(CIPHERTEXT_LENGTH + IV_LENGTH2);
    const passwordBytes = utf8ToBytes3(password);
    const seed = sha5122(passwordBytes);
    const derivedKeyPair = ml_kem768.keygen(seed);
    const sharedSecret = ml_kem768.decapsulate(cipherText, derivedKeyPair.secretKey);
    const cipher = gcm2(sharedSecret, iv);
    const decrypted = cipher.decrypt(encrypted);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    throw new DecryptError(error);
  }
};

// src/encryption/mlkem2.ts
var mlkem2_exports = {};
__export(mlkem2_exports, {
  decrypt: () => decrypt3,
  encrypt: () => encrypt3
});
import { ml_kem768 as ml_kem7682 } from "@noble/post-quantum/ml-kem";
import { randomBytes as randomBytes4 } from "@noble/post-quantum/utils";
import { concatBytes as concatBytes3, utf8ToBytes as utf8ToBytes4 } from "@noble/hashes/utils";
import { sha3_512 } from "@noble/hashes/sha3";
import { chacha20poly1305 } from "@noble/ciphers/chacha";
var IV_LENGTH3 = 12;
var CIPHERTEXT_LENGTH2 = 1088;
var SALT_LENGTH2 = 32;
var deriveKey = async (password, salt) => {
  const passwordBytes = utf8ToBytes4(password);
  const key = await sha3_512(concatBytes3(passwordBytes, salt));
  return key;
};
var encrypt3 = async (content, password) => {
  try {
    const salt = randomBytes4(SALT_LENGTH2);
    const derivedKey = await deriveKey(password, salt);
    const keyPair = ml_kem7682.keygen(derivedKey);
    const { cipherText, sharedSecret } = ml_kem7682.encapsulate(keyPair.publicKey);
    const iv = randomBytes4(IV_LENGTH3);
    const encryptedContent = chacha20poly1305(sharedSecret, iv).encrypt(utf8ToBytes4(content));
    const result = concatBytes3(salt, cipherText, iv, encryptedContent);
    return Buffer2.from(result).toString("base64");
  } catch (error) {
    throw new EncryptError(error);
  }
};
var decrypt3 = async (encryptedContent, password) => {
  try {
    const data = Buffer2.from(encryptedContent, "base64");
    const salt = data.subarray(0, SALT_LENGTH2);
    const cipherText = data.subarray(SALT_LENGTH2, SALT_LENGTH2 + CIPHERTEXT_LENGTH2);
    const iv = data.subarray(
      SALT_LENGTH2 + CIPHERTEXT_LENGTH2,
      SALT_LENGTH2 + CIPHERTEXT_LENGTH2 + IV_LENGTH3
    );
    const encrypted = data.subarray(SALT_LENGTH2 + CIPHERTEXT_LENGTH2 + IV_LENGTH3);
    const derivedKey = await deriveKey(password, salt);
    const keyPair = ml_kem7682.keygen(derivedKey);
    const sharedSecret = ml_kem7682.decapsulate(cipherText, keyPair.secretKey);
    const decrypted = chacha20poly1305(sharedSecret, iv).decrypt(encrypted);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    throw new DecryptError(error);
  }
};

// src/encryption/mlkem_argon2.ts
var mlkem_argon2_exports = {};
__export(mlkem_argon2_exports, {
  decrypt: () => decrypt4,
  encrypt: () => encrypt4
});
import { ml_kem768 as ml_kem7683 } from "@noble/post-quantum/ml-kem";
import { randomBytes as randomBytes5 } from "@noble/post-quantum/utils";
import { concatBytes as concatBytes4, utf8ToBytes as utf8ToBytes5 } from "@noble/hashes/utils";
import { argon2idAsync as argon2idAsync2 } from "@noble/hashes/argon2";
import { chacha20poly1305 as chacha20poly13052 } from "@noble/ciphers/chacha";
var IV_LENGTH4 = 12;
var CIPHERTEXT_LENGTH3 = 1088;
var SALT_LENGTH3 = 32;
var deriveKey2 = async (password, salt) => {
  return argon2idAsync2(utf8ToBytes5(password), salt, getArgon2ContentParams());
};
var encrypt4 = async (content, password) => {
  try {
    const salt = randomBytes5(SALT_LENGTH3);
    const derivedKey = await deriveKey2(password, salt);
    const keyPair = ml_kem7683.keygen(derivedKey);
    const { cipherText, sharedSecret } = ml_kem7683.encapsulate(keyPair.publicKey);
    const iv = randomBytes5(IV_LENGTH4);
    const encryptedContent = chacha20poly13052(sharedSecret, iv).encrypt(utf8ToBytes5(content));
    const result = concatBytes4(salt, cipherText, iv, encryptedContent);
    return Buffer2.from(result).toString("base64");
  } catch (error) {
    throw new EncryptError(error);
  }
};
var decrypt4 = async (encryptedContent, password) => {
  try {
    const data = Buffer2.from(encryptedContent, "base64");
    const salt = data.subarray(0, SALT_LENGTH3);
    const cipherText = data.subarray(SALT_LENGTH3, SALT_LENGTH3 + CIPHERTEXT_LENGTH3);
    const iv = data.subarray(
      SALT_LENGTH3 + CIPHERTEXT_LENGTH3,
      SALT_LENGTH3 + CIPHERTEXT_LENGTH3 + IV_LENGTH4
    );
    const encrypted = data.subarray(SALT_LENGTH3 + CIPHERTEXT_LENGTH3 + IV_LENGTH4);
    const derivedKey = await deriveKey2(password, salt);
    const keyPair = ml_kem7683.keygen(derivedKey);
    const sharedSecret = ml_kem7683.decapsulate(cipherText, keyPair.secretKey);
    const decrypted = chacha20poly13052(sharedSecret, iv).decrypt(encrypted);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    throw new DecryptError(error);
  }
};

// src/encryption/registry.ts
import { deflate, inflate } from "pako";
var encryptionRegistry = {
  "aes-256-gcm": {
    name: "aes-256-gcm",
    encrypt,
    decrypt
  },
  "ml-kem-768": {
    name: "ml-kem-768",
    encrypt: encrypt2,
    decrypt: decrypt2
  },
  "ml-kem-768-2": {
    name: "ml-kem-768-2",
    encrypt: encrypt3,
    decrypt: decrypt3
  },
  "ml-kem-768-argon2": {
    name: "ml-kem-768-argon2",
    encrypt: encrypt4,
    decrypt: decrypt4
  }
};
var compressionRegistry = {
  "zlib:pako": {
    name: "zlib:pako",
    compress: deflate,
    decompress: inflate
  },
  none: {
    name: "none",
    compress: (data) => data,
    decompress: (data) => data
  }
};
function validateMetadata(metadata) {
  if (metadata.compression?.algorithm && !compressionRegistry[metadata.compression.algorithm]) {
    throw new Error(
      `Compression algorithm ${metadata.compression.algorithm} not found in registry`
    );
  }
  if (!metadata.encryption?.algorithm || !encryptionRegistry[metadata.encryption.algorithm]) {
    throw new Error(`Encryption algorithm ${metadata.encryption?.algorithm} not found in registry`);
  }
}

// src/encryption/machine.ts
var machine_exports = {};
__export(machine_exports, {
  decrypt: () => decrypt5,
  encrypt: () => encrypt5
});
import { gcm as aesGcm } from "@noble/ciphers/aes";
import { hkdf } from "@noble/hashes/hkdf";
import { sha256 as sha2562 } from "@noble/hashes/sha2";
import { randomBytes as randomBytes6, concatBytes as concatBytes5, utf8ToBytes as utf8ToBytes6 } from "@noble/hashes/utils";
var SALT_LENGTH4 = 16;
var IV_LENGTH5 = 12;
var KEY_LENGTH2 = 32;
var SCHEME_PREFIX = "m1:";
var HKDF_INFO = utf8ToBytes6("crypt.fyi/machine-metadata");
var deriveKey3 = (key, salt) => hkdf(sha2562, utf8ToBytes6(key), salt, HKDF_INFO, KEY_LENGTH2);
var encrypt5 = async (content, key) => {
  try {
    const salt = randomBytes6(SALT_LENGTH4);
    const iv = randomBytes6(IV_LENGTH5);
    const derivedKey = deriveKey3(key, salt);
    const cipher = aesGcm(derivedKey, iv);
    const encrypted = cipher.encrypt(utf8ToBytes6(content));
    const result = concatBytes5(salt, iv, encrypted);
    return SCHEME_PREFIX + Buffer2.from(result).toString("base64");
  } catch (error) {
    throw new EncryptError(error);
  }
};
var decrypt5 = async (encryptedContent, key) => {
  if (!encryptedContent.startsWith(SCHEME_PREFIX)) {
    return decrypt(encryptedContent, key);
  }
  try {
    const data = Buffer2.from(encryptedContent.slice(SCHEME_PREFIX.length), "base64");
    const salt = data.subarray(0, SALT_LENGTH4);
    const iv = data.subarray(SALT_LENGTH4, SALT_LENGTH4 + IV_LENGTH5);
    const ciphertext = data.subarray(SALT_LENGTH4 + IV_LENGTH5);
    const derivedKey = deriveKey3(key, salt);
    const cipher = aesGcm(derivedKey, iv);
    const decrypted = cipher.decrypt(ciphertext);
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    throw new DecryptError(error);
  }
};

// src/client.ts
import { inflate as inflate2 } from "pako";
var Client = class {
  constructor({
    apiUrl,
    keyLength = 32,
    xClient
  }) {
    this.apiUrl = apiUrl;
    this.keyLength = keyLength;
    this.xClient = xClient;
  }
  getHeaders() {
    const headers = {
      "Content-Type": "application/json"
    };
    if (this.xClient) {
      headers["X-Client"] = this.xClient;
    }
    return headers;
  }
  async processContent(content, metadata, key) {
    validateMetadata(metadata);
    let processed = content;
    if (metadata.compression?.algorithm) {
      const algorithm = compressionRegistry[metadata.compression.algorithm];
      const compressed = algorithm.compress(new TextEncoder().encode(processed));
      processed = Buffer2.from(compressed).toString("base64");
    }
    if (metadata.encryption?.algorithm) {
      const algorithm = encryptionRegistry[metadata.encryption.algorithm];
      processed = await algorithm.encrypt(processed, key);
    }
    return processed;
  }
  // Metadata for the inner (URL key) layer: carries compression + the primary
  // encryption algorithm.
  keyLayerMetadata(metadata) {
    return {
      compression: metadata.compression,
      encryption: { algorithm: metadata.encryption.algorithm }
    };
  }
  // Metadata for the outer (user password) layer: encryption only, no
  // compression (compression happens once, on the inner layer).
  passwordLayerMetadata(metadata) {
    return {
      encryption: {
        algorithm: metadata.encryption.passwordAlgorithm ?? metadata.encryption.algorithm
      }
    };
  }
  async recoverContent(encoded, key, metadata) {
    if (!metadata) {
      const decrypted = JSON.parse(await gcm_exports.decrypt(encoded, key));
      if ("metadata" in decrypted) {
        let processedContent = Buffer2.from(decrypted.data, "base64");
        if (decrypted.metadata.compression?.algorithm === "zlib:pako") {
          const decompressed = inflate2(processedContent);
          processedContent = Buffer2.from(decompressed);
        }
        return new TextDecoder().decode(processedContent);
      }
      return decrypted;
    }
    validateMetadata(metadata);
    let recovered = encoded;
    if (metadata.encryption?.algorithm) {
      const algorithm = encryptionRegistry[metadata.encryption.algorithm];
      recovered = await algorithm.decrypt(recovered, key);
    }
    if (metadata.compression?.algorithm) {
      const algorithm = compressionRegistry[metadata.compression.algorithm];
      const decompressed = algorithm.decompress(Buffer2.from(recovered, "base64"));
      recovered = new TextDecoder().decode(decompressed);
    }
    return recovered;
  }
  async create(input) {
    const rawKey = await generateRandomString(this.keyLength);
    const hasPassword = input.p !== void 0 && input.p !== "";
    const metadata = input.m ?? {
      compression: {
        algorithm: "zlib:pako"
      },
      encryption: {
        algorithm: "ml-kem-768-2",
        passwordAlgorithm: hasPassword ? "ml-kem-768-argon2" : void 0
      }
    };
    let processed;
    if (!hasPassword) {
      processed = await this.processContent(input.c, metadata, rawKey);
    } else if (metadata.encryption.passwordAlgorithm) {
      const inner = await this.processContent(input.c, this.keyLayerMetadata(metadata), rawKey);
      processed = await this.processContent(
        inner,
        this.passwordLayerMetadata(metadata),
        input.p
      );
    } else {
      const inner = await this.processContent(input.c, metadata, rawKey);
      processed = await this.processContent(inner, metadata, input.p);
    }
    const key = hasPassword ? `${KEY_VERSION_2_PREFIX}${rawKey}` : rawKey;
    const hash = hasPassword ? await deriveVerificationHash("v2", rawKey, input.p) : await deriveVerificationHash("legacy", rawKey);
    const response = await fetch(`${this.apiUrl}/vault`, {
      method: "POST",
      headers: this.getHeaders(),
      body: JSON.stringify({
        c: processed,
        h: hash,
        m: metadata,
        b: input.b,
        ttl: input.ttl,
        ips: input.ips,
        rc: input.rc,
        fc: input.fc,
        wh: input.wh ? {
          u: input.wh.u,
          n: input.wh.n,
          r: input.wh.r,
          fpk: input.wh.fpk,
          fip: input.wh.fip,
          b: input.wh.b
        } : void 0
      })
    });
    if (!response.ok) {
      throw new ErrorUnexpectedStatus(response.status);
    }
    const data = await response.json();
    return {
      ...data,
      key,
      hash
    };
  }
  async read(id, key, password) {
    const { scheme, rawKey } = parseKey(key);
    const h = await deriveVerificationHash(scheme, rawKey, password);
    const res = await fetch(`${this.apiUrl}/vault/${id}?h=${h}`, {
      headers: this.getHeaders()
    });
    if (!res.ok) {
      if (res.status === 400) {
        throw new ErrorInvalidKeyAndOrPassword();
      } else if (res.status === 404) {
        throw new ErrorNotFound();
      }
      throw new ErrorUnexpectedStatus(res.status);
    }
    const data = await res.json();
    let decrypted;
    if (!password) {
      decrypted = await this.recoverContent(data.c, rawKey, data.m);
    } else if (data.m?.encryption?.passwordAlgorithm) {
      const tmp = await this.recoverContent(data.c, password, this.passwordLayerMetadata(data.m));
      decrypted = await this.recoverContent(tmp, rawKey, this.keyLayerMetadata(data.m));
    } else {
      const tmp = await this.recoverContent(data.c, password, data.m);
      decrypted = await this.recoverContent(tmp, rawKey, data.m);
    }
    return {
      c: decrypted,
      burned: data.b,
      cd: data.cd,
      ttl: data.ttl
    };
  }
  async delete(id, dt) {
    const response = await fetch(`${this.apiUrl}/vault/${id}`, {
      method: "DELETE",
      headers: this.getHeaders(),
      body: JSON.stringify({ dt })
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new ErrorNotFound();
      }
      throw new ErrorUnexpectedStatus(response.status);
    }
  }
  async exists(id) {
    const response = await fetch(`${this.apiUrl}/vault/${id}`, {
      method: "HEAD",
      headers: this.getHeaders()
    });
    if (!response.ok) {
      if (response.status === 404) {
        return false;
      }
      throw new ErrorUnexpectedStatus(response.status);
    }
    return true;
  }
};
var ErrorNotFound = class extends Error {
  constructor() {
    super("secret not found");
  }
};
var ErrorUnexpectedStatus = class extends Error {
  constructor(status) {
    super(`unexpected status code ${status}`);
    this.status = status;
  }
};
var ErrorInvalidKeyAndOrPassword = class extends Error {
  constructor() {
    super("invalid key and/or password");
  }
};

// src/sleep.ts
var sleep = (ms, { enabled = true } = {}) => enabled ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve();
var sleepWithCancel = (ms, { enabled = true } = {}) => {
  if (!enabled) {
    return {
      promise: Promise.resolve(),
      cancel: () => {
      }
      // No-op for disabled sleep
    };
  }
  let timeoutId;
  const promise = new Promise((resolve) => {
    timeoutId = setTimeout(resolve, ms);
  });
  return {
    promise,
    cancel: () => {
      clearTimeout(timeoutId);
    }
  };
};

// src/fetchRetry.ts
var createFetchRetryClient = ({
  maxAttempts,
  requestTimeoutMs = 5e3,
  fetchFn = fetch,
  shouldRetry = isRetryableFetchError,
  calculateBackoff = defaultCalculateBackoff
}) => {
  return async (url, options) => {
    let attempts = 0;
    const errors = [];
    do {
      const signals = [options?.signal];
      let timeoutId;
      if (requestTimeoutMs > 0) {
        const ab = new AbortController();
        timeoutId = setTimeout(() => ab.abort(new TimeoutError()), requestTimeoutMs);
        signals.push(ab.signal);
      }
      const signal = AbortSignal.any(signals.filter((s) => !!s));
      try {
        const response = await fetchFn(url, { ...options, signal });
        if (!response.ok) {
          throw new HTTPError(response.status, response.statusText);
        }
        return response;
      } catch (error) {
        errors.push(error);
        if (!shouldRetry(error)) {
          throw new FetchRetryError(errors, "Non-retryable error occurred");
        }
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
      const backoffMs = calculateBackoff(attempts);
      const sleeper = sleepWithCancel(backoffMs);
      const eventListenerAC = new AbortController();
      await Promise.race([
        sleeper.promise,
        new Promise(
          (resolve) => signal.addEventListener("abort", resolve, { signal: eventListenerAC.signal, once: true })
        )
      ]);
      sleeper.cancel();
      eventListenerAC.abort();
    } while (++attempts < maxAttempts);
    throw new FetchRetryError(errors, "Failed to fetch after max retries");
  };
};
var isRetryableFetchError = (error) => {
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof TimeoutError) {
    return true;
  }
  return error instanceof HTTPError && isRetryableStatusCode(error.status);
};
var isRetryableStatusCode = (status) => {
  if (status >= 500 && status <= 599) return true;
  if (status === 429) return true;
  return [408, 423, 425].includes(status);
};
var defaultCalculateBackoff = (attempt) => {
  const backoffMs = 5e3 * Math.pow(2, attempt - 1);
  return Math.min(backoffMs, 6e4);
};
var HTTPError = class extends Error {
  constructor(status, message) {
    super(`${status} ${message}`);
    this.status = status;
    this.name = "HTTPError";
  }
};
var TimeoutError = class extends Error {
  constructor() {
    super();
    this.name = "TimeoutError";
  }
};
var FetchRetryError = class extends Error {
  constructor(errors, message) {
    super(message);
    this.errors = errors;
    this.name = "FetchRetryError";
  }
  toString() {
    return this.errors.map((error) => error instanceof Error ? error.message : error).join("\n");
  }
};
export {
  Client,
  DecryptError,
  EncryptError,
  ErrorInvalidKeyAndOrPassword,
  ErrorNotFound,
  ErrorUnexpectedStatus,
  FetchRetryError,
  HTTPError,
  KEY_VERSION_2_PREFIX,
  SsrfError,
  TimeoutError,
  URL_SAFE_DICTIONARY,
  compressionRegistry,
  createFetchRetryClient,
  createVaultRequestSchema,
  createVaultResponseSchema,
  de,
  deleteVaultParamsSchema,
  deleteVaultRequestSchema,
  deriveVerificationHash,
  en,
  encryptionRegistry,
  es,
  fr,
  gcm_exports as gcm,
  generateRandomBytes,
  generateRandomHexString,
  generateRandomString,
  isPrivateIp,
  isRetryableFetchError,
  isValidWebhookUrl,
  machine_exports as machine,
  mlkem_exports as mlkem,
  mlkem2_exports as mlkem2,
  mlkem_argon2_exports as mlkemArgon2,
  parseKey,
  processingMetadataSchema,
  readVaultParamsSchema,
  readVaultQuerySchema,
  readVaultResponseSchema,
  resources,
  sha256,
  sha512,
  sleep,
  sleepWithCancel,
  supportedLanguages,
  supportedLanguagesOptions,
  validateMetadata,
  validateWebhookUrl,
  vaultValueSchema,
  zh
};
